<template>
  <AuthGuard @authenticated="handleAuthenticated">
    <!-- Side Menu -->
    <SideMenu 
      :is-open="sideMenuOpen"
      :selected-category="selectedCategory"
      :category-labels="categoryLabels"
      @close="sideMenuOpen = false"
      @navigate="handleMenuNavigation"
    />

    <!-- API Loading State -->
    <div v-if="!apiLoaded" class="api-loading-overlay">
      <div class="loading-content">
        <LoadingProgress :current-stage="loadingStage" :completed-stages="completedStages" />
      </div>
    </div>

    <!-- Custom Header -->
    <CustomHeader :is-configured="isConfigured" :today-remaining-budget="todayRemainingBudget"
      :selected-category="selectedCategory" :category-labels="categoryLabels" :category-order="categoryOrder"
      :menu-open="sideMenuOpen"
      @edit-budget="editBudgetSetup" @reset-app="confirmResetApp"
      @signed-out="handleSignedOut" @processing-started="handleProcessingStarted"
      @transaction-processed="handleTransactionProcessed"
      @transactions-confirmed="handleTransactionsConfirmed" @update:selected-category="selectedCategory = $event"
      @menu-opened="sideMenuOpen = true" />

    <!-- Transaction Toast -->
    <TransactionToast ref="transactionToast" />

    <!-- Error Modal -->
    <ErrorModal :show="showErrorModal" :error="currentError" @close="hideError" />

    <!-- Global Loading Modal -->
    <GlobalLoadingModal />

    <!-- Quick Transaction Tab (Desktop Only) -->
    <QuickTransactionTab 
      v-if="isConfigured && !showOnboarding && !showNaturalLanguageSetup"
      ref="quickTransactionTab"
      @processing-started="handleProcessingStarted"
      @transaction-processed="handleTransactionProcessed"
      @transactions-confirmed="handleTransactionsConfirmed"
    />

    <div v-if="apiLoaded" class="fixed-container" :class="{ 'dark-mode': isDarkMode, 'ptop': isConfigured, 'menu-open': sideMenuOpen}">
      <div class="container-fluid mb-5 pt-3">
        <!-- Initial Setup Section (only when not configured) -->
        <transition name="fade" mode="out-in">
          <div v-if="!isLoading && !isConfigured && !showOnboarding && !showNaturalLanguageSetup" key="setup"
            class="setup-container">
            <MonthlyExpensesBreakdown :expenses="monthlyExpenses" :gross-income="grossMonthlyIncome"
              :is-setup-mode="true" @update:grossIncome="grossMonthlyIncome = $event" @add-expense="addMonthlyExpense"
              @remove-expense="removeMonthlyExpense" @complete-setup="setupBudget" />
          </div>

          <!-- Natural Language Budget Setup -->
          <div v-else-if="!isLoading && !isConfigured && showNaturalLanguageSetup" key="natural-setup"
            class="setup-container">
            <NaturalLanguageBudget @budget-processed="handleBudgetProcessed" @use-manual-setup="useManualSetup" />
          </div>

          <!-- Onboarding Flow (only when not configured and first time) -->
          <div v-else-if="!isLoading && !isConfigured && showOnboarding" key="onboarding" class="onboarding-container">
            <OnboardingFlow @start-setup="startBudgetSetup" @start-natural-setup="startNaturalLanguageSetup" />
          </div>

          <!-- Budget Dashboard -->
          <div v-else-if="!isLoading && isConfigured && !isLoadingTransactions" key="dashboard" class="mb-5">
            
            <!-- Month Navigator (Mobile at top, Desktop floating) -->
            <MonthNavigator 
              v-if="isConfigured && !showOnboarding && !showNaturalLanguageSetup"
              :menu-open="sideMenuOpen"
            />

            <div v-if="isEditingBudget" class="modal-overlay" @click.self="cancelEdit">
              <div class="modal-content">
                <div class="modal-header">
                  <h4 class="modal-title">Edit Monthly Income</h4>
                  <button type="button" class="btn-close" @click="cancelEdit" aria-label="Close">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>

                <div class="modal-body">
                  <!-- Gross Monthly Income -->
                  <div class="mb-4">
                    <label for="editGrossMonthlyIncome" class="form-label fw-semibold">
                      <i class="bi bi-cash-stack me-1"></i>Monthly Income (Gross)
                    </label>
                    <div class="input-group input-group-lg">
                      <span class="input-group-text currency-symbol">$</span>
                      <input type="number" class="form-control" id="editGrossMonthlyIncome"
                        v-model.number="grossMonthlyIncome" placeholder="Enter your gross monthly income" step="0.01">
                    </div>
                    <div class="form-text">
                      <i class="bi bi-info-circle me-1"></i>
                      This is your total monthly income before expenses. Use the Monthly Expenses card in your dashboard
                      to manage your expenses.
                    </div>
                  </div>

                  <!-- Current Budget Summary (Read-only) -->
                  <div class="budget-summary">
                    <h6 class="mb-3">
                      <i class="bi bi-calculator me-1"></i>Current Budget Summary
                    </h6>
                    <div class="summary-card">
                      <div class="summary-row">
                        <span class="summary-label">Gross Income:</span>
                        <span class="summary-value income">${{ (grossMonthlyIncome || 0).toFixed(2) }}</span>
                      </div>
                      <div class="summary-row">
                        <span class="summary-label">Total Expenses:</span>
                        <span class="summary-value expense">-${{ totalMonthlyExpenses.toFixed(2) }}</span>
                      </div>
                      <div class="divider"></div>
                      <div class="summary-row net">
                        <span class="summary-label">Net Monthly Income:</span>
                        <span class="summary-value" :class="monthlyIncome > 0 ? 'positive' : 'negative'">
                          ${{ monthlyIncome.toFixed(2) }}
                        </span>
                      </div>
                    </div>
                    <div class="form-text mt-2">
                      <i class="bi bi-lightbulb me-1"></i>
                      To modify expenses, use the "Monthly Expenses" card in your dashboard.
                    </div>
                  </div>
                </div>

                <div class="modal-footer">
                  <button @click="cancelEdit" class="btn btn-outline-secondary">Cancel</button>
                  <button @click="setupBudget" class="btn btn-primary"
                    :disabled="!grossMonthlyIncome || grossMonthlyIncome <= 0">
                    <i class="bi bi-check-lg me-1"></i>Save Changes
                  </button>
                </div>
              </div>
            </div>

            <!-- Alert Modal -->
            <div v-if="showAlertModal" class="modal-overlay" @click.self="closeAlertModal">
              <div class="modal-content alert-modal">
                <div class="modal-header">
                  <h5 class="modal-title">
                    <i class="bi bi-info-circle me-2" v-if="alertType === 'info'"></i>
                    <i class="bi bi-check-circle me-2 text-success" v-if="alertType === 'success'"></i>
                    <i class="bi bi-exclamation-triangle me-2 text-warning" v-if="alertType === 'warning'"></i>
                    <i class="bi bi-x-circle me-2 text-danger" v-if="alertType === 'error'"></i>
                    <i class="bi bi-question-circle me-2 text-primary" v-if="alertType === 'confirm'"></i>
                    {{ alertTitle }}
                  </h5>
                  <button type="button" class="btn-close" @click="closeAlertModal" aria-label="Close">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>

                <div class="modal-body">
                  <p class="mb-0">{{ alertMessage }}</p>
                </div>

                <div class="modal-footer">
                  <button v-if="alertType === 'confirm'" @click="cancelConfirmation"
                    class="btn btn-outline-secondary">Cancel
                  </button>
                  <button v-if="alertType === 'confirm'" @click="confirmAction" class="btn btn-danger">
                    <i class="bi bi-trash me-1"></i>Delete
                  </button>
                  <button v-if="alertType !== 'confirm'" @click="closeAlertModal" class="btn btn-primary">OK</button>
                </div>
              </div>
            </div>

            <!-- Edit Transaction Modal -->
            <div v-if="editingTransaction" class="modal-overlay" @click.self="cancelTransactionEdit">
              <div class="modal-content">
                <div class="modal-header">
                  <h4 class="modal-title">Edit Transaction</h4>
                  <button type="button" class="btn-close" @click="cancelTransactionEdit" aria-label="Close">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>

                <div class="modal-body">
                  <div class="mb-3">
                    <label for="editTransactionDescription" class="form-label fw-semibold">
                      <i class="bi bi-card-text me-1"></i>Description
                    </label>
                    <input 
                      type="text" 
                      class="form-control form-control-lg" 
                      id="editTransactionDescription"
                      v-model="editingTransactionData.description"
                      placeholder="Transaction description"
                      @keyup.enter="saveTransactionEdit"
                    >
                  </div>

                  <div class="mb-3">
                    <label for="editTransactionAmount" class="form-label fw-semibold">
                      <i class="bi bi-currency-dollar me-1"></i>Amount
                    </label>
                    <div class="input-group input-group-lg">
                      <span class="input-group-text currency-symbol">$</span>
                      <input 
                        type="number" 
                        step="0.01" 
                        class="form-control" 
                        id="editTransactionAmount"
                        v-model.number="editingTransactionData.amount"
                        placeholder="0.00"
                        @keyup.enter="saveTransactionEdit"
                      >
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="editTransactionCategory" class="form-label fw-semibold">
                      <i class="bi bi-tag me-1"></i>Category
                    </label>
                    <input 
                      type="text" 
                      class="form-control form-control-lg" 
                      id="editTransactionCategory"
                      v-model="editingTransactionData.category"
                      placeholder="e.g., Groceries, Gas, Entertainment"
                      @keyup.enter="saveTransactionEdit"
                    >
                  </div>
                </div>

                <div class="modal-footer">
                  <button @click="cancelTransactionEdit" class="btn btn-outline-secondary">
                    <i class="bi bi-x-lg me-1"></i>Cancel
                  </button>
                  <button 
                    @click="saveTransactionEdit" 
                    class="btn btn-primary"
                    :disabled="!editingTransactionData.description || !editingTransactionData.amount || editingTransactionData.amount <= 0"
                  >
                    <i class="bi bi-check-lg me-1"></i>Save Changes
                  </button>
                </div>
              </div>
            </div>

            <!-- Add Transaction Form -->
            <div v-if="false" class="add-transaction-card card mb-3">
              <div class="card-header">
                <div class="header-icon">
                  <i class="fa-solid fa-plus"></i>
                </div>
                <div class="header-content">
                  <h3 class="card-title">Add Transaction</h3>
                  <p class="card-subtitle">Track your expenses and income</p>
                </div>
                <!-- <div class="header-actions">
              <input type="file" id="csvFileInput" accept="*.csv" ref="fileInput" class="d-none"
                @change="importFromCSV">
              <button @click="triggerFileInput" class="btn btn-outline-secondary btn-sm">
                <i class="bi bi-upload me-1"></i>Import CSV
              </button>
            </div> -->
              </div>

              <div class="card-body">
                <!-- Transaction Type Selection -->
                <div class="transaction-type-selector mb-4">
                  <label class="form-label fw-semibold mb-3">Transaction Type</label>
                  <div class="type-buttons">
                    <div class="type-option" :class="{ active: newTransaction.type === 'debit' }">
                      <input class="form-check-input d-none" type="radio" name="transactionType" id="debitType"
                        value="debit" v-model="newTransaction.type">
                      <label class="type-button expense-button" for="debitType">
                        <div class="type-icon">
                          <i class="bi bi-arrow-down-circle"></i>
                        </div>
                        <div class="type-content">
                          <span class="type-title">Expense</span>
                          <span class="type-description">Money spent</span>
                        </div>
                      </label>
                    </div>
                    <div class="type-option" :class="{ active: newTransaction.type === 'credit' }">
                      <input class="form-check-input d-none" type="radio" name="transactionType" id="creditType"
                        value="credit" v-model="newTransaction.type">
                      <label class="type-button income-button" for="creditType">
                        <div class="type-icon">
                          <i class="bi bi-arrow-up-circle"></i>
                        </div>
                        <div class="type-content">
                          <span class="type-title">Income</span>
                          <span class="type-description">Money received</span>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Transaction Details -->
                <div class="transaction-details">
                  <div class="row g-3">
                    <div class="col-12 col-md-6">
                      <label for="transactionDescription" class="form-label fw-semibold">
                        <i class="bi bi-card-text me-1"></i>Description
                      </label>
                      <div ref="descriptionInput" class="form-control form-control-lg description-input"
                        contenteditable="true" @input="handleDescriptionInput" @paste="handlePaste"
                        @keydown="handleKeydown" data-placeholder="What was this transaction for?"></div>
                    </div>
                    <div class="col-12 col-md-3">
                      <label for="transactionAmount" class="form-label fw-semibold">
                        <i class="bi bi-currency-dollar me-1"></i>Amount
                      </label>
                      <div class="input-group input-group-lg">
                        <span class="input-group-text currency-symbol">$</span>
                        <input type="number" step="0.01" class="form-control" id="transactionAmount"
                          v-model.number="newTransaction.amount" placeholder="0.00">
                      </div>
                    </div>
                    <div class="col-12 col-md-3">
                      <label for="transactionDate" class="form-label fw-semibold">
                        <i class="bi bi-calendar3 me-1"></i>Date
                      </label>
                      <input type="date" class="form-control form-control-lg" id="transactionDate"
                        v-model="transactionDateInput">
                    </div>
                    <div class="col-12">
                      <label for="transactionCategory" class="form-label fw-semibold">
                        <i class="bi bi-tag me-1"></i>Category
                      </label>
                      <input type="text" class="form-control form-control-lg" id="transactionCategory"
                        v-model="newTransaction.category" placeholder="e.g., Groceries, Gas, Entertainment">
                    </div>
                  </div>
                </div>

                <!-- Advanced Options -->
                <div class="advanced-options mt-4">
                  <div class="advanced-toggle">
                    <div class="form-check form-switch mt-4">
                      <input class="form-check-input" type="checkbox" id="splitTransactionCheck"
                        v-model="splitTransaction.enabled">
                      <label class="form-check-label fw-semibold" for="splitTransactionCheck">
                        <i class="bi bi-calendar2-range me-1"></i>Split across multiple days
                      </label>
                    </div>
                    <div class="form-check form-switch mt-3">
                      <input class="form-check-input" type="checkbox" id="keywordLearningCheck"
                        v-model="keywordLearningEnabled">
                      <label class="form-check-label fw-semibold" for="keywordLearningCheck">
                        <i class="bi bi-lightbulb me-1"></i>Auto-learn keywords
                      </label>
                    </div>
                    <div class="form-check form-switch mt-3">
                      <input class="form-check-input" type="checkbox" id="aiCategorizationCheck"
                        v-model="aiCategorizationEnabled">
                      <label class="form-check-label fw-semibold" for="aiCategorizationCheck">
                        <i class="bi bi-cpu me-1"></i>AI categorization
                      </label>
                    </div>
                  </div>

                  <div v-if="splitTransaction.enabled" class="split-options mt-3">
                    <div class="split-container">
                      <div class="row g-3 align-items-end">
                        <div class="col-12 col-md-4">
                          <label for="splitDays" class="form-label fw-semibold">Number of days</label>
                          <input type="number" min="2" max="31" class="form-control" id="splitDays"
                            v-model.number="splitTransaction.days" placeholder="2">
                        </div>
                        <div class="col-12 col-md-8">
                          <div class="split-preview">
                            <div class="preview-content">
                              <span class="preview-label">Amount per day:</span>
                              <span class="preview-amount">
                                ${{ splitTransaction.enabled && newTransaction.amount ?
                                  (newTransaction.amount / splitTransaction.days).toFixed(2) : '0.00' }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Action Button -->
                <div class="action-section mt-4 pt-3">
                  <button @click="addTransaction" class="btn btn-primary btn-lg w-100"
                    :disabled="!newTransaction.description || !newTransaction.amount || isAddingTransaction"
                    :class="{ 'btn-success': newTransaction.type === 'credit', 'btn-danger': newTransaction.type === 'debit' }">
                    <span v-if="isAddingTransaction" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>
                    <i v-else class="bi bi-plus-lg me-2"></i>
                    <span v-if="isAddingTransaction">Creating transaction...</span>
                    <span v-else>
                      {{ splitTransaction.enabled ?
                        `Add Split Transaction (${splitTransaction.days} days)` :
                        `Add ${newTransaction.type === 'credit' ? 'Income' : 'Expense'}` }}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Loading State for Transaction Data -->
            <div v-if="isLoadingTransactions" class="loading-container text-center py-5">
              <div class="spinner-border text-primary mb-3" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="text-muted">Loading your transaction data...</p>
            </div>

            <!-- Dashboard Cards with Category Filter (Desktop Only) -->
            <div v-if="!isLoadingTransactions" class="dashboard-container">
              <!-- API Loading State -->
              <div v-if="!apiLoaded" class="api-loading-container mb-3">
                <div class="card">
                  <div class="card-header">
                    <div class="header-icon">
                      <i class="fa-solid fa-sync-alt"></i>
                    </div>
                    <div class="header-content">
                      <h3 class="card-title">Loading Dashboard</h3>
                      <p class="card-subtitle">Preparing your budget data</p>
                    </div>
                  </div>
                  <div class="card-body text-center py-4">
                    <div class="loading-spinner mb-3">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>
                    <p class="text-muted mb-0">Syncing your transactions and budget information...</p>
                  </div>
                </div>
              </div>

              <!-- Dashboard Cards with Masonry Layout -->
              <div v-if="apiLoaded" class="dashboard-masonry" 
                   @touchstart="swipeGesture.handleTouchStart" 
                   @touchmove="swipeGesture.handleTouchMove" 
                   @touchend="swipeGesture.handleTouchEnd">

                <template v-for="card in filteredDashboardCards" :key="card.component">
                  <div class="masonry-item" 
                    :class="{ 'hide-on-mobile-past-month': card.component === 'TodayRemaining' && !budgetData.isViewingCurrentMonth.value }">
                    <!-- Natural Language Transaction Form -->
                    <NaturalLanguageTransaction v-if="card.component === 'NaturalLanguageTransaction'"
                      :auto-confirm="true"
                      @processing-started="handleProcessingStarted"
                      @transaction-processed="handleTransactionProcessed"
                      @transactions-confirmed="handleTransactionsConfirmed" />

                    <!-- TodayRemaining Card -->
                    <TodayRemaining v-if="card.component === 'TodayRemaining'"
                      :today-remaining-budget="todayRemainingBudget" :daily-budget="dailyBudget"
                      :today-credits="todayCredits" :today-debits="todayDebits" 
                      @switch-to-savings="switchToSavingsCategory" />

                    <!-- FinancialAssistantChat Card -->
                    <FinancialAssistantChat v-if="card.component === 'FinancialAssistantChat'" />

                    <!-- CategorySpendingLimits Card -->
                    <CategorySpendingLimits v-if="card.component === 'CategorySpendingLimits'"
                      :transactions="currentMonthTransactions" />

                    <!-- CategoryExpensesChart Card -->
                    <CategoryExpensesChart v-if="card.component === 'CategoryExpensesChart'"
                      :transactions="currentMonthTransactions"
                      :selected-month="budgetData.currentDate.value.getMonth()"
                      :selected-year="budgetData.currentDate.value.getFullYear()" />

                    <!-- CategoryKeywords Card -->
                    <CategoryKeywords v-if="card.component === 'CategoryKeywords'" :user="user" />

                    <!-- AverageDailySaved Card -->
                    <AverageDailySaved v-if="card.component === 'AverageDailySaved'"
                      :transactions="currentMonthTransactions" :daily-budget="dailyBudget"
                      :current-savings-goal="savingsGoal" @averageSavedUpdated="updateAverageSaved" />

                    <!-- AverageDailySpending Card -->
                    <AverageDailySpending v-if="card.component === 'AverageDailySpending'"
                      :transactions="currentMonthTransactions" :daily-budget="dailyBudget" />

                    <!-- RunningSavings Card -->
                    <RunningSavings v-if="card.component === 'RunningSavings'" :transactions="currentMonthTransactions"
                      :daily-budget="dailyBudget" />

                    <!-- DailySavingsGoal Card -->
                    <DailySavingsGoal v-if="card.component === 'DailySavingsGoal'"
                      :transactions="currentMonthTransactions" :daily-budget="dailyBudget"
                      :current-savings-goal="savingsGoal" @updateSavingsGoal="updateGoal" />

                    <!-- SavingsGoals Card -->
                    <SavingsGoals v-if="card.component === 'SavingsGoals'" @transaction-created="handleSavingsContribution" />

                    <!-- MonthlyExpensesChart Card -->
                    <MonthlyExpensesChart v-if="card.component === 'MonthlyExpensesChart'"
                      :monthly-expenses="monthlyExpenses"
                      :gross-monthly-income="grossMonthlyIncome" />

                    <!-- MonthlyExpensesBreakdown Card -->
                    <MonthlyExpensesBreakdown v-if="card.component === 'MonthlyExpensesBreakdown'"
                      :expenses="monthlyExpenses" 
                      :is-read-only="!budgetData.isViewingCurrentMonth.value"
                      @add-expense="addMonthlyExpense"
                      @remove-expense="removeMonthlyExpense" />

                    <!-- MonthlySavingsChart Card -->
                    <MonthlySavingsChart v-if="card.component === 'MonthlySavingsChart'" />

                    <!-- DailyExpensesChart Card -->
                    <DailyExpensesChart v-if="card.component === 'DailyExpensesChart'"
                      :transactions="currentMonthTransactions" :daily-budget="dailyBudget" />
                  </div>
                </template>
              </div>
            </div>

            <!-- Transaction Filter -->
            <div v-if="!isLoadingTransactions" class="card p-3 mb-3">
              <div class="row align-items-center">
                <div class="col">
                  <h5 class="mb-0">Transaction History</h5>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-md-6">
                  <div class="input-group">
                    <span class="input-group-text search-icon">
                      <i class="bi bi-search"></i>
                    </span>
                    <input type="text" class="form-control" v-model="transactionFilter"
                      placeholder="Filter transactions by description..." @input="filterTransactions">
                    <button v-if="transactionFilter" @click="clearFilter" class="btn btn-outline-secondary"
                      type="button">
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                </div>
                <div class="col-md-6 mt-2 mt-md-0 d-flex align-items-center">
                  <small class="text-muted">
                    {{ filteredTransactions.length > 0 ? filteredTransactions.length : currentMonthTransactions.length
                    }} of {{ currentMonthTransactions.length }} current month transactions
                  </small>
                </div>
              </div>

              <!-- Transaction History Stats -->
              <div v-if="transactionFilter && filteredTransactions.length > 0" class="row mt-3">
                <div class="col-12">
                  <div class="transaction-stats-card">
                    <div class="stats-header">
                      <i class="bi bi-bar-chart me-2"></i>
                      <span class="stats-title">Filtered Transaction Stats</span>
                    </div>
                    <div class="stats-content">
                      <div class="stat-item">
                        <span class="stat-label">Total Amount:</span>
                        <span class="stat-value" :class="filteredTransactionSum >= 0 ? 'text-success' : 'text-danger'">
                          <span v-if="filteredTransactionSum < 0">-</span>${{
                            Math.abs(filteredTransactionSum).toFixed(2)
                          }}
                        </span>
                      </div>
                      <div class="stat-item">
                        <span class="stat-label">Expenses:</span>
                        <span class="stat-value text-danger">
                          -${{ filteredExpenseSum.toFixed(2) }}
                        </span>
                      </div>
                      <div class="stat-item">
                        <span class="stat-label">Income:</span>
                        <span class="stat-value text-success">
                          +${{ filteredIncomeSum.toFixed(2) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Transaction History -->
            <div v-if="!isLoadingTransactions" class="transaction-history-container">
              <template v-for="dayGroup in transactionsByDay" :key="dayGroup.date.toDateString()">
                <!-- Day Header Card -->
                <div class="day-header-card nested-container">
                  <div class="day-header-content">
                    <div class="day-date">
                      <i class="bi bi-calendar3 me-2"></i>
                      {{ formatDayHeader(dayGroup.date) }}
                    </div>
                    <div class="day-summary">
                      <span v-if="dayGroup.totalExpenses > 0" class="expense-summary">
                        -${{ dayGroup.totalExpenses.toFixed(2) }} spent
                      </span>
                      <span v-if="dayGroup.totalIncome > 0" class="income-summary">
                        +${{ dayGroup.totalIncome.toFixed(2) }} earned
                      </span>
                      <span v-if="dayGroup.totalSavings > 0" class="income-summary">
                        +${{ dayGroup.totalSavings.toFixed(2) }} saved
                      </span>
                      <span v-if="dayGroup.totalExpenses === 0 && dayGroup.totalIncome === 0" class="neutral-summary">
                        No transactions
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Transactions for this day -->
                <div class="transaction-card-modern" v-for="transaction in dayGroup.transactions"
                  :key="transaction.originalIndex">
                  <div class="transaction-content">
                    <!-- Category Icon -->
                    <span class="transaction-icon" :style="getCategoryIconStyle(transaction.category)">
                      <i :class="getCategoryIcon(transaction.category)"></i>
                    </span>
                    
                    <!-- Transaction Details -->
                    <div class="transaction-details">
                      <p class="transaction-name">{{ transaction.description }}</p>
                      <p class="transaction-meta">
                        <span v-if="transaction.category">{{ transaction.category }} · </span>
                        <span>{{ formatTableDateSecondary(transaction.date) }}</span>
                      </p>
                    </div>
                    
                    <!-- Amount and Actions -->
                    <div class="transaction-actions">
                      <div class="transaction-amount-wrapper">
                        <i v-if="transaction.type === 'credit'" class="bi bi-arrow-down-left transaction-arrow income-arrow"></i>
                        <i v-else class="bi bi-arrow-up-right transaction-arrow expense-arrow"></i>
                        <span class="transaction-amount" :class="{
                          'amount-income': transaction.type === 'credit',
                          'amount-expense': transaction.type === 'debit',
                          'amount-savings': transaction.type === 'savings'
                        }">
                          <span v-if="transaction.type === 'credit'">+</span>
                          <span v-if="transaction.type === 'debit'">-</span>
                          ${{ transaction.amount.toFixed(2) }}
                        </span>
                      </div>
                      
                      <!-- Action Buttons -->
                      <div v-if="budgetData.isViewingCurrentMonth.value" class="transaction-buttons">
                        <button v-if="transaction.type !== 'savings'" @click="startEditTransaction(transaction)" class="btn-icon edit-btn" title="Edit transaction">
                          <i class="bi bi-pencil-fill"></i>
                        </button>
                        <button @click="deleteTransaction(transaction)" class="btn-icon delete-btn" title="Delete transaction">
                          <i class="bi bi-trash-fill"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <div v-if="currentMonthTransactions.length === 0" class="empty-state-card card">
                <div class="card-body text-center py-5">
                  <i class="bi bi-inbox display-4 text-muted mb-3"></i>
                  <p class="text-muted mb-0">No transactions this month</p>
                  <small class="text-muted">Add your first transaction above to get started</small>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </AuthGuard>
</template>

<script>
import CustomHeader from "./components/CustomHeader.vue";
import SideMenu from "./components/SideMenu.vue";
import TodayRemaining from "./components/TodayRemaining.vue";
import DailyBudgetRemaining from "./components/DailyBudgetAdjusted.vue";
import dailyBudgetAdjusted from "./components/DailyBudgetAdjusted.vue";
import AverageDailySpending from "./components/AverageDailySpending.vue";
import AverageDailySaved from "./components/AverageDailySaved.vue";
import RunningSavings from "./components/RunningSavings.vue";
import DailySavingsGoal from "./components/DailySavingsGoal.vue";
import DailyExpensesChart from "./components/DailyExpensesChart.vue";
import MonthlySavingsChart from "./components/MonthlySavingsChart.vue";
import MonthlyExpensesChart from "./components/MonthlyExpensesChart.vue";
import MonthlyExpensesBreakdown from "./components/MonthlyExpensesBreakdown.vue";
import CategoryExpensesChart from "./components/CategoryExpensesChart.vue";
import CategoryKeywords from "./components/CategoryKeywords.vue";
import CategorySpendingLimits from "./components/CategorySpendingLimits.vue";
import SavingsGoals from "./components/SavingsGoals.vue";
import TransactionToast from "./components/TransactionToast.vue";
import OnboardingFlow from "./components/OnboardingFlow.vue";
import NaturalLanguageBudget from "./components/NaturalLanguageBudget.vue";
import NaturalLanguageTransaction from "./components/NaturalLanguageTransaction.vue";
import QuickTransactionTab from "./components/QuickTransactionTab.vue";
import MonthNavigator from "./components/MonthNavigator.vue";
import AuthGuard from "./components/AuthGuard.vue";
import ErrorModal from "./components/ErrorModal.vue";
import LoadingProgress from "./components/LoadingProgress.vue";
import GlobalLoadingModal from "./components/GlobalLoadingModal.vue";
import FinancialAssistantChat from "./components/FinancialAssistantChat.vue";
import { Capacitor } from '@capacitor/core';
import { NavigationBar, NavigationBarColor } from '@capgo/capacitor-navigation-bar';
import { useDarkMode } from './composables/useDarkMode';
import { useTextHighlighting } from './composables/useTextHighlighting';
import { useCategoryKeywords } from './composables/useCategoryKeywords';
import { useAuth } from './composables/useAuth';
import { useErrorModal } from './composables/useErrorModal';
import { useGlobalLoading } from './composables/useGlobalLoading';
import { getCaretPosition, setCaretPosition, escapeHtml } from './utils/caretUtils';
import { autoCategorizeTransaction } from './utils/transactionCategorization';
import { createTransaction, getTransactions, deleteTransaction as deleteTransactionAPI, updateTransaction as updateTransactionAPI } from './utils/transactionService.ts';
import { getBudget, storeBudget } from './utils/budgetService.ts';
import { getUserConfig, updateUserConfig } from './utils/userConfigService.ts';
import { getKeywordMappings } from './utils/keywordService.ts';
import { useBudgetData } from "./composables/useBudgetData.js";
import { useSavingsGoals } from "./composables/useSavingsGoals.js";
import { useSwipeGesture } from "./composables/useSwipeGesture.js";
import { useRemainingBudget } from "./composables/useRemainingBudget.js";

export default {
  name: 'App',
  components: {
    CustomHeader,
    SideMenu,
    DailySavingsGoal,
    RunningSavings,
    AverageDailySaved,
    AverageDailySpending,
    DailyBudgetRemaining,
    TodayRemaining,
    DailyExpensesChart,
    MonthlySavingsChart,
    MonthlyExpensesChart,
    MonthlyExpensesBreakdown,
    CategoryExpensesChart,
    CategoryKeywords,
    CategorySpendingLimits,
    SavingsGoals,
    TransactionToast,
    OnboardingFlow,
    NaturalLanguageBudget,
    NaturalLanguageTransaction,
    QuickTransactionTab,
    MonthNavigator,
    AuthGuard,
    ErrorModal,
    LoadingProgress,
    GlobalLoadingModal,
    FinancialAssistantChat
  },
  setup() {
    const { isDarkMode, toggleDarkMode } = useDarkMode();
    const highlighting = useTextHighlighting();
    const { learnFromDescription, autoCategorize } = useCategoryKeywords();
    const { user, getAccessToken } = useAuth();
    const { showErrorModal, currentError, showApiError, hideError } = useErrorModal();
    const loadingIndicator = useGlobalLoading();
    const budgetData = useBudgetData();
    const savingsGoals = useSavingsGoals();
    const { adjustedRemainingBudget } = useRemainingBudget();

    // Initialize swipe gesture handlers with callbacks
    const swipeGesture = useSwipeGesture({
      swipeThreshold: 50,
      minVelocity: 0.3,
      onSwipeLeft: () => {
        // Will be set up in mounted() with proper context
      },
      onSwipeRight: () => {
        // Will be set up in mounted() with proper context
      }
    });

    return {
      isDarkMode,
      toggleDarkMode,
      highlighting,
      learnFromDescription,
      autoCategorize,
      user,
      getAccessToken,
      showErrorModal,
      currentError,
      showApiError,
      loadingIndicator,
      hideError,
      budgetData,
      savingsGoals,
      swipeGesture,
      adjustedRemainingBudget
    };
  },
  data() {
    return {
      isLoading: true,
      isConfigured: false,
      showOnboarding: false,
      showNaturalLanguageSetup: true,
      isEditingBudget: false,
      grossMonthlyIncome: 0,
      monthlyExpenses: [],
      newExpense: {
        name: '',
        amount: null
      },
      savingsGoal: 0,
      currentAverageSaved: 0,
      transactions: [],
      transactionDateInput: '',
      newTransaction: {
        description: '',
        amount: null,
        type: 'debit',
        date: new Date(),
        category: ''
      },
      splitTransaction: {
        enabled: false,
        days: 2
      },
      keywordLearningEnabled: true,
      aiCategorizationEnabled: false,
      showAlertModal: false,
      alertType: null,
      alertTitle: '',
      alertMessage: '',
      pendingDeleteTransaction: null,
      pendingConfirmAction: null,
      transactionFilter: '',
      filteredTransactions: [],
      editingTransaction: null,
      editingTransactionData: {
        description: '',
        amount: null,
        category: ''
      },
      lastParsedDescription: '',
      isAddingTransaction: false,
      selectedCategory: 'budget',
      cardCategories: {
        'NaturalLanguageTransaction': 'budget',
        'TodayRemaining': 'budget',
        'FinancialAssistantChat': 'budget',
        'CategorySpendingLimits': 'categories',
        'CategoryExpensesChart': 'spending',
        'CategoryKeywords': 'categories',
        'AverageDailySaved': 'savings',
        'AverageDailySpending': 'spending',
        'RunningSavings': 'savings',
        'DailySavingsGoal': 'savings',
        'SavingsGoals': 'savings',
        'MonthlyExpensesChart': 'budget',
        'MonthlyExpensesBreakdown': 'budget',
        'MonthlySavingsChart': 'spending',
        'DailyExpensesChart': 'spending'
      },
      categoryLabels: {
        'all': 'All Cards',
        'budget': 'Budget & Income',
        'spending': 'Spending Analysis',
        'savings': 'Savings & Goals',
        'categories': 'Categories & Keywords'
      },
      hasLoadedInitialData: false,
      isLoadingTransactions: true,
      apiLoaded: false,
      loadingStage: 'config',
      completedStages: [],
      // Mobile navigation
      categoryOrder: ['all', 'budget', 'spending', 'savings', 'categories'],
      // Side menu state - open by default on desktop
      sideMenuOpen: false
    }
  },
  computed: {
    dailyBudgetAdjusted() {
      return dailyBudgetAdjusted
    },
    // Get days in current month
    daysInMonth() {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    },

    // Calculate daily budget
    dailyBudget() {
      return this.monthlyIncome / this.daysInMonth;
    },

    // Calculate total debits
    totalDebits() {
      return this.currentMonthTransactions
        .filter(t => t.type === 'debit')
        .reduce((sum, t) => sum + t.amount, 0);
    },

    // Calculate total credits (additional income beyond monthly income)
    totalCredits() {
      return this.currentMonthTransactions
        .filter(t => t.type === 'credit')
        .reduce((sum, t) => sum + t.amount, 0);
    },

    // Calculate remaining budget
    remainingBudget() {
      return this.monthlyIncome + this.totalCredits - this.totalDebits;
    },

    // Calculate daily remaining budget
    dailyRemainingBudget() {
      const today = new Date();
      const remainingDays = this.daysInMonth - today.getDate() + 1;
      return this.remainingBudget / remainingDays;
    },

    // Calculate today's debits
    todayDebits() {
      const today = new Date();
      return this.currentMonthTransactions
        .filter(t => {
          const transactionDate = new Date(t.date);
          return t.type === 'debit' &&
            transactionDate.getDate() === today.getDate() &&
            transactionDate.getMonth() === today.getMonth() &&
            transactionDate.getFullYear() === today.getFullYear();
        })
        .reduce((sum, t) => sum + t.amount, 0);
    },

    // Calculate today's credits
    todayCredits() {
      const today = new Date();
      return this.currentMonthTransactions
        .filter(t => {
          const transactionDate = new Date(t.date);
          return t.type === 'credit' &&
            transactionDate.getDate() === today.getDate() &&
            transactionDate.getMonth() === today.getMonth() &&
            transactionDate.getFullYear() === today.getFullYear();
        })
        .reduce((sum, t) => sum + t.amount, 0);
    },

    // Calculate remaining daily budget for today
    todayRemainingBudget() {
      return this.dailyBudget - (this.todayDebits - this.todayCredits);
    },

    // Calculate total monthly expenses
    totalMonthlyExpenses() {
      return this.monthlyExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    },

    // Calculate monthly income after expenses
    monthlyIncome() {
      return Math.max(0, (this.grossMonthlyIncome || 0) - this.totalMonthlyExpenses);
    },

    // Group transactions by day
    transactionsByDay() {
      const grouped = {};
      const transactionsToGroup = this.filteredTransactions.length > 0 || this.transactionFilter ? this.filteredTransactions : this.currentMonthTransactions;

      transactionsToGroup.forEach((transaction, index) => {
        const date = new Date(transaction.date);
        const dayKey = date.toDateString(); // e.g., "Mon Oct 23 2023"

        if (!grouped[dayKey]) {
          grouped[dayKey] = {
            date: date,
            transactions: [],
            totalExpenses: 0,
            totalIncome: 0,
            totalSavings: 0
          };
        }

        // Store the full transaction object for reliable deletion by ID or properties
        grouped[dayKey].transactions.push({ ...transaction });

        if (transaction.type === 'debit') {
          grouped[dayKey].totalExpenses += transaction.amount;
        } else if (transaction.type === 'credit') {
          grouped[dayKey].totalIncome += transaction.amount;
        } else if (transaction.type === 'savings') {
          grouped[dayKey].totalSavings += transaction.amount;
        }
      });

      // Convert to array, sort days by newest first, and sort transactions within each day by newest first (using ID)
      return Object.values(grouped)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .map(dayGroup => ({
          ...dayGroup,
          transactions: dayGroup.transactions.sort((a, b) => {
            // Sort by ID if available (contains timestamp), otherwise fall back to date
            if (a.id && b.id) {
              return b.id.localeCompare(a.id); // Lexicographic sort works for this ID format
            }
            return new Date(b.date) - new Date(a.date);
          })
        }));
    },

    // Calculate sum of filtered transactions (income - expenses)
    filteredTransactionSum() {
      if (!this.transactionFilter || this.filteredTransactions.length === 0) {
        return 0;
      }

      return this.filteredTransactions.reduce((sum, transaction) => {
        return transaction.type === 'credit'
          ? sum + transaction.amount
          : sum - transaction.amount;
      }, 0);
    },

    // Calculate sum of filtered expenses
    filteredExpenseSum() {
      if (!this.transactionFilter || this.filteredTransactions.length === 0) {
        return 0;
      }

      return this.filteredTransactions
        .filter(t => t.type === 'debit')
        .reduce((sum, t) => sum + t.amount, 0);
    },

    // Calculate sum of filtered income
    filteredIncomeSum() {
      if (!this.transactionFilter || this.filteredTransactions.length === 0) {
        return 0;
      }

      return this.filteredTransactions
        .filter(t => t.type === 'credit')
        .reduce((sum, t) => sum + t.amount, 0);
    },

    // Filter transactions to current month only
    currentMonthTransactions() {
      return this.budgetData.currentMonthTransactions.value;
    },

    // Get available category labels based on viewing mode
    availableCategoryLabels() {
      // When viewing past months, exclude savings and categories
      if (!this.budgetData.isViewingCurrentMonth.value) {
        const { savings, categories, ...availableLabels } = this.categoryLabels;
        return availableLabels;
      }
      return this.categoryLabels;
    },

    // Get filtered dashboard cards based on selected category
    filteredDashboardCards() {
      const allCards = [
        // Only show NaturalLanguageTransaction on mobile (since desktop has the floating tab)
        { component: 'NaturalLanguageTransaction', category: 'budget', mobileOnly: true },
        { component: 'TodayRemaining', category: 'budget' },
        // Only show FinancialAssistantChat on desktop (hide on mobile)
        { component: 'FinancialAssistantChat', category: 'budget', desktopOnly: true },
        { component: 'CategorySpendingLimits', category: 'categories' },
        { component: 'CategoryExpensesChart', category: 'spending' },
        { component: 'CategoryKeywords', category: 'categories' },
        { component: 'AverageDailySaved', category: 'savings' },
        { component: 'AverageDailySpending', category: 'spending' },
        { component: 'RunningSavings', category: 'savings' },
        { component: 'SavingsGoals', category: 'savings' },
        { component: 'MonthlyExpensesChart', category: 'budget' },
        { component: 'MonthlyExpensesBreakdown', category: 'budget' },
        { component: 'MonthlySavingsChart', category: 'spending' },
        { component: 'DailyExpensesChart', category: 'spending' }
      ];

      let filteredCards = allCards;
      
      // When viewing past months, exclude NaturalLanguageTransaction, savings and categories cards
      if (!this.budgetData.isViewingCurrentMonth.value) {
        filteredCards = filteredCards.filter(card => 
          card.component !== 'NaturalLanguageTransaction' &&
          card.category !== 'savings' && 
          card.category !== 'categories'
        );
      }
      
      // Filter by category if not 'all'
      if (this.selectedCategory !== 'all') {
        filteredCards = filteredCards.filter(card => card.category === this.selectedCategory);
      }

      // Filter out mobile-only cards on desktop (lg and above)
      if (window.innerWidth >= 1200) {
        filteredCards = filteredCards.filter(card => !card.mobileOnly);
      }

      // Filter out desktop-only cards on mobile
      if (window.innerWidth < 1200) {
        filteredCards = filteredCards.filter(card => !card.desktopOnly);
      }

      return filteredCards;
    }
  },
  methods: {
    // Get card count for a specific category
    getCategoryCardCount(categoryKey) {
      const allCards = [
        { component: 'NaturalLanguageTransaction', category: 'budget', mobileOnly: true },
        { component: 'TodayRemaining', category: 'budget' },
        { component: 'FinancialAssistantChat', category: 'budget', desktopOnly: true },
        { component: 'CategorySpendingLimits', category: 'categories' },
        { component: 'CategoryExpensesChart', category: 'spending' },
        { component: 'CategoryKeywords', category: 'categories' },
        { component: 'AverageDailySaved', category: 'savings' },
        { component: 'AverageDailySpending', category: 'spending' },
        { component: 'RunningSavings', category: 'savings' },
        { component: 'DailySavingsGoal', category: 'savings' },
        { component: 'SavingsGoals', category: 'savings' },
        { component: 'MonthlyExpensesChart', category: 'budget' },
        { component: 'MonthlyExpensesBreakdown', category: 'budget' },
        { component: 'MonthlySavingsChart', category: 'spending' },
        { component: 'DailyExpensesChart', category: 'spending' }
      ];

      let availableCards = allCards;

      // When viewing past months, exclude NaturalLanguageTransaction, savings and categories cards
      if (!this.budgetData.isViewingCurrentMonth.value) {
        availableCards = availableCards.filter(card => 
          card.component !== 'NaturalLanguageTransaction' &&
          card.category !== 'savings' && 
          card.category !== 'categories'
        );
      }

      // Filter out mobile-only cards on desktop
      if (window.innerWidth >= 1200) {
        availableCards = availableCards.filter(card => !card.mobileOnly);
      }

      // Filter out desktop-only cards on mobile
      if (window.innerWidth < 1200) {
        availableCards = availableCards.filter(card => !card.desktopOnly);
      }

      if (categoryKey === 'all') {
        return availableCards.length;
      }

      return availableCards.filter(card => card.category === categoryKey).length;
    },

    navigateToNextCategory() {
      const currentIndex = this.categoryOrder.indexOf(this.selectedCategory);
      if (currentIndex < this.categoryOrder.length - 1) {
        this.selectedCategory = this.categoryOrder[currentIndex + 1];
      }
    },

    navigateToPreviousCategory() {
      const currentIndex = this.categoryOrder.indexOf(this.selectedCategory);
      if (currentIndex > 0) {
        this.selectedCategory = this.categoryOrder[currentIndex - 1];
      }
    },

    switchToSavingsCategory() {
      this.selectedCategory = 'savings';
      // Scroll to top smoothly
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    // Handle menu navigation from side menu
    handleMenuNavigation(category) {
      this.selectedCategory = category;
      // Scroll to top smoothly
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    // Trigger pulse animation on quick transaction tab
    triggerQuickTransactionPulse() {
      if (this.$refs.quickTransactionTab && window.innerWidth >= 1200) {
        this.$refs.quickTransactionTab.triggerPulse();
      }
    },

    // Handle window resize for responsive card filtering
    handleResize() {
      // Force reactivity update for card counts and filtering
      this.$forceUpdate();
    },

    // Get category icon based on category name
    getCategoryIcon(category) {
      if (!category) return 'bi bi-tag-fill';
      
      const categoryLower = category.toLowerCase();
      const iconMap = {
        'groceries': 'bi bi-cart-fill',
        'food': 'bi bi-cart-fill',
        'shopping': 'bi bi-cart-fill',
        'housing': 'bi bi-house-fill',
        'rent': 'bi bi-house-fill',
        'mortgage': 'bi bi-house-fill',
        'transport': 'bi bi-car-front-fill',
        'gas': 'bi bi-fuel-pump-fill',
        'car': 'bi bi-car-front-fill',
        'utilities': 'bi bi-lightning-charge-fill',
        'electric': 'bi bi-lightning-charge-fill',
        'water': 'bi bi-droplet-fill',
        'entertainment': 'bi bi-controller',
        'dining': 'bi bi-cup-hot-fill',
        'coffee': 'bi bi-cup-hot-fill',
        'restaurant': 'bi bi-cup-hot-fill',
        'income': 'bi bi-graph-up-arrow',
        'salary': 'bi bi-graph-up-arrow',
        'savings': 'bi bi-piggy-bank-fill',
        'health': 'bi bi-heart-pulse-fill',
        'medical': 'bi bi-heart-pulse-fill',
        'fitness': 'bi bi-heart-pulse-fill',
        'internet': 'bi bi-wifi',
        'phone': 'bi bi-phone-fill',
        'subscription': 'bi bi-arrow-repeat'
      };

      // Check for partial matches
      for (const [key, icon] of Object.entries(iconMap)) {
        if (categoryLower.includes(key)) {
          return icon;
        }
      }

      return 'bi bi-tag-fill';
    },

    // Get category icon style (background and text color)
    getCategoryIconStyle(category) {
      if (!category) {
        return {
          backgroundColor: 'var(--light-border)',
          color: 'var(--light-text-muted)'
        };
      }
      
      const categoryLower = category.toLowerCase();
      const colorMap = {
        'groceries': 'var(--chart-color-3)',
        'food': 'var(--chart-color-3)',
        'shopping': 'var(--chart-color-11)',
        'housing': 'var(--chart-color-2)',
        'rent': 'var(--chart-color-2)',
        'mortgage': 'var(--chart-color-2)',
        'transport': 'var(--chart-color-10)',
        'gas': 'var(--chart-color-10)',
        'car': 'var(--chart-color-10)',
        'utilities': 'var(--chart-color-6)',
        'electric': 'var(--chart-color-6)',
        'water': 'var(--chart-color-5)',
        'entertainment': 'var(--chart-color-1)',
        'dining': 'var(--chart-color-4)',
        'coffee': 'var(--chart-color-4)',
        'restaurant': 'var(--chart-color-4)',
        'income': 'var(--chart-color-5)',
        'salary': 'var(--chart-color-5)',
        'savings': 'var(--chart-color-5)',
        'health': 'var(--chart-color-12)',
        'medical': 'var(--chart-color-12)',
        'fitness': 'var(--chart-color-12)',
        'internet': 'var(--chart-color-8)',
        'phone': 'var(--chart-color-8)',
        'subscription': 'var(--chart-color-9)'
      };

      // Check for partial matches
      for (const [key, color] of Object.entries(colorMap)) {
        if (categoryLower.includes(key)) {
          return {
            backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
            color: color
          };
        }
      }

      // Default color
      return {
        backgroundColor: 'var(--light-border)',
        color: 'var(--light-text-muted)'
      };
    },

    // Check if desktop view and open menu accordingly
    checkDesktopView() {
      if (window.innerWidth >= 769) {
        this.sideMenuOpen = true;
      } else {
        this.sideMenuOpen = false;
      }
    },
    // Authentication handlers
    async handleAuthenticated() {
      // User has been authenticated, the watcher will handle reloading data
      // This method is called when user signs in through the auth modal
      await this.loadFromStorage();
    },

    handleSignedOut() {
      // User signed out, clear local data and reset app state
      this.savingsGoals.clearGoals(); // Clear savings goals
      this.resetAppState();
    },

    resetAppState() {
      // Reset all app state to initial values
      this.isConfigured = false;
      this.showOnboarding = false;
      this.showNaturalLanguageSetup = true;
      this.grossMonthlyIncome = 0;
      this.monthlyExpenses = [];
      this.savingsGoal = 0;
      this.transactionFilter = '';
      this.filteredTransactions = [];
      this.newTransaction = {
        description: '',
        amount: null,
        type: 'debit',
        date: new Date(),
        category: ''
      };
      this.splitTransaction = {
        enabled: false,
        days: 2
      };
      this.keywordLearningEnabled = true;
      this.aiCategorizationEnabled = false;

      // Clear localStorage
      localStorage.removeItem('budgetApp');
    },

    async updateGoal(newGoal) {
      this.savingsGoal = newGoal;
      this.budgetData.updateSavingsGoal(newGoal);
      this.saveToLocalStorage();

      // Save to API if user is authenticated
      if (this.user) {
        try {
          await this.saveUserConfigToAPI();
          console.log('Savings goal updated and synced to API');
        } catch (error) {
          console.error('Failed to sync savings goal to API:', error);
          // Continue with local storage - don't block user experience
        }
      }
    },

    updateAverageSaved(averageSaved) {
      this.currentAverageSaved = averageSaved;
    },

    // Filter transactions by description
    filterTransactions() {
      if (!this.transactionFilter.trim()) {
        this.filteredTransactions = [];
        return;
      }

      const filterText = this.transactionFilter.toLowerCase().trim();
      this.filteredTransactions = this.budgetData.currentMonthTransactions.value
        .filter(transaction =>
          transaction.description.toLowerCase().includes(filterText)
        );
    },

    // Clear the filter
    clearFilter() {
      this.transactionFilter = '';
      this.filteredTransactions = [];
    },

    // Handle description input with highlighting
    handleDescriptionInput(event) {
      const element = event.target;
      const text = element.innerText;
      this.newTransaction.description = text;

      // Find matches in the text
      const matches = this.highlighting.findTextMatches(text);

      // Get current highlight state
      const highlightState = this.highlighting.getHighlightState(element, matches);

      // Update DOM if needed, otherwise just update values
      if (highlightState.needsUpdate) {
        // Extract values before applying highlights
        this.updateValuesFromMatches(matches);
        this.highlighting.applyAllHighlights(element, text, matches);
      } else {
        this.updateValuesFromMatches(matches);
      }
    },

    // Update transaction values from matches without touching DOM
    updateValuesFromMatches(matches) {
      if (matches.price) {
        const amount = parseFloat(matches.price[1]);
        if (!isNaN(amount) && amount > 0) {
          this.newTransaction.amount = amount;
        }
      }
      if (matches.date) {
        this.updateDateFromKeyword(matches.date[1]);
      }
      if (matches.tag) {
        this.newTransaction.category = matches.tag[1].toLowerCase();
      }
    },

    // Update transaction date based on keyword
    updateDateFromKeyword(keyword) {
      const today = new Date();
      let targetDate;

      switch (keyword.toLowerCase()) {
        case 'yesterday':
          targetDate = new Date(today);
          targetDate.setDate(today.getDate() - 1);
          break;
        case 'today':
          targetDate = new Date(today);
          break;
        case 'tomorrow':
          targetDate = new Date(today);
          targetDate.setDate(today.getDate() + 1);
          break;
        default:
          return;
      }

      // Update the date input field
      const year = targetDate.getFullYear();
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      this.transactionDateInput = `${year}-${month}-${day}`;
      this.newTransaction.date = targetDate;
    },

    // Handle paste to strip formatting
    handlePaste(event) {
      event.preventDefault();
      const text = event.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
    },

    // Handle keydown for Enter key
    handleKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        // Optionally trigger add transaction
        if (this.newTransaction.description && this.newTransaction.amount) {
          this.addTransaction();
        }
      }
    },



    // Set default date input to current date
    setDefaultDateInput() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');

      this.transactionDateInput = `${year}-${month}-${day}`;
    },

    // Create date from string - preserve API timestamps exactly as-is
    createDateFromString(dateString) {
      if (!dateString) return new Date();
      
      // API always returns ISO timestamps - preserve them exactly
      if (dateString.includes('T')) {
        return new Date(dateString);
      }
      
      // Legacy support for date-only strings (YYYY-MM-DD) from localStorage
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return new Date(`${dateString}T12:00:00`);
      }
      
      // Fallback for any other format
      console.warn('Unexpected date format:', dateString);
      return new Date(dateString);
    },

    // Start budget setup from onboarding
    startBudgetSetup() {
      this.showOnboarding = false;
      this.saveToLocalStorage();
    },

    // Start natural language budget setup
    startNaturalLanguageSetup() {
      this.showOnboarding = false;
      this.showNaturalLanguageSetup = true;
      this.saveToLocalStorage();
    },

    // Handle processed budget from natural language component
    handleBudgetProcessed(processedData) {
      // Set the gross income and expenses from AI processing
      this.grossMonthlyIncome = processedData.grossIncome || 0;
      this.monthlyExpenses = processedData.expenses || [];

      // Update the composables with the new data
      this.budgetData.updateGrossIncome(this.grossMonthlyIncome);
      this.budgetData.updateMonthlyExpenses(this.monthlyExpenses);

      // Handle budget start date if provided (Requirement 1.1, 1.3, 1.4)
      // Only set budgetStartDate if it doesn't already exist (immutability)
      if (processedData.budgetStartDate && !this.budgetData.budgetStartDate.value) {
        this.saveBudgetStartDate(processedData.budgetStartDate);
        this.budgetData.setBudgetStartDate(processedData.budgetStartDate);
      }

      // Complete the setup process
      window.scrollTo(0, 0);
      this.isConfigured = true;
      this.showNaturalLanguageSetup = false;
      this.showOnboarding = false;
      this.isEditingBudget = false;

      // Save to API if user is authenticated
      if (this.user) {
        this.saveBudgetToAPI();
        this.saveUserConfigToAPI();
      }

      this.saveToLocalStorage();
    },

    // Switch to manual setup from natural language
    useManualSetup() {
      this.showNaturalLanguageSetup = false;
      this.saveToLocalStorage();
    },

    // Setup budget
    async setupBudget() {
      if (this.monthlyIncome > 0) {
        window.scrollTo(0, 0);
        this.isConfigured = true;
        this.showOnboarding = false;
        this.isEditingBudget = false;

        // Save to API if user is authenticated
        if (this.user) {
          try {
            await this.saveBudgetToAPI();
            await this.saveUserConfigToAPI();
            console.log('Budget and config saved to API successfully');
          } catch (error) {
            console.error('Failed to save to API:', error);
            this.showApiError(error);
          }
        }

        this.saveToLocalStorage();
      } else {
        this.showAlert('warning', 'Invalid Income', 'Please enter a valid monthly income');
      }
    },

    // Add transaction
    async addTransaction() {
      if (!this.newTransaction.description || !this.newTransaction.amount) {
        this.showAlert('warning', 'Missing Information', 'Please fill in all fields');
        return;
      }

      // Validate split transaction settings
      if (this.splitTransaction.enabled && (!this.splitTransaction.days || this.splitTransaction.days < 2)) {
        this.showAlert('warning', 'Invalid Split Settings', 'Please enter a valid number of days (minimum 2) for splitting');
        return;
      }

      // Show global loading modal
      this.loadingIndicator.showTransactionSaving();
      this.isAddingTransaction = true;

      try {
        // Stage 1: Validating Data
        this.loadingIndicator.updateStage('validating', []);
        await new Promise(resolve => setTimeout(resolve, 300));

        // Clean the description by removing highlighted keywords
        const cleanedDescription = this.highlighting.cleanDescription(this.newTransaction.description);

        // Use custom date if provided, otherwise use current date
        const transactionDate = this.transactionDateInput ? new Date(`${this.transactionDateInput}T00:00:00`) : new Date();

        // Stage 2: Auto-Categorizing
        this.loadingIndicator.completeStage('validating');
        this.loadingIndicator.updateStage('categorizing', ['validating']);

        // Auto-categorize if category is not manually set
        let finalCategory = this.newTransaction.category || '';
        let wasAutoCategorized = false;
        if (!finalCategory && cleanedDescription) {
          // Always try local keyword-based categorization first
          const localCategory = this.autoCategorize(cleanedDescription);
          if (localCategory) {
            finalCategory = localCategory;
            wasAutoCategorized = true;
          } else if (this.aiCategorizationEnabled) {
            console.log('attempt');
            // Fall back to API categorization only if AI is enabled
            try {
              const enriched = await autoCategorizeTransaction({
                description: cleanedDescription,
                amount: this.newTransaction.amount,
                date: transactionDate
              }, this.aiCategorizationEnabled);
              if (enriched.category) {
                finalCategory = enriched.category;
                wasAutoCategorized = true;
              }
              console.log(JSON.stringify(enriched));
            } catch (error) {
              console.error('Auto-categorization failed:', error);
              this.loadingIndicator.hideLoading();
              this.showApiError(error);
              return;
            }
          }
        }

        // Learn from manually added category
        let learnedKeywords = { count: 0, keywords: [] };
        if (this.keywordLearningEnabled && finalCategory && cleanedDescription) {
          try {
            const accessToken = await this.getAccessToken();
            learnedKeywords = await this.learnFromDescription(cleanedDescription, finalCategory, accessToken);
            if (learnedKeywords.count > 0) {
              console.log(`Learned ${learnedKeywords.count} new keyword(s) for category "${finalCategory}": ${learnedKeywords.keywords.join(', ')}`);
            }
          } catch (error) {
            console.error('Failed to learn keywords:', error);
            // Continue without learning if API fails
          }
        }

        // Stage 3: Saving Transaction
        this.loadingIndicator.completeStage('categorizing');
        this.loadingIndicator.updateStage('saving', ['validating', 'categorizing']);
        await new Promise(resolve => setTimeout(resolve, 200));

        if (this.splitTransaction.enabled) {
          // Create multiple transactions for split
          const amountPerDay = parseFloat(this.newTransaction.amount) / this.splitTransaction.days;
          const transactionTemplates = [];

          for (let i = 0; i < this.splitTransaction.days; i++) {
            const splitDate = new Date(transactionDate);
            splitDate.setDate(splitDate.getDate() + i);

            const transaction = {
              description: `${cleanedDescription} (${i + 1}/${this.splitTransaction.days})`,
              amount: parseFloat(amountPerDay.toFixed(2)),
              type: this.newTransaction.type,
              date: splitDate,
              category: finalCategory
            };

            transactionTemplates.push(transaction);
          }

          // Call API for each split transaction first, then add to local array
          if (this.user) {
            this.loadingIndicator.completeStage('saving');
            this.loadingIndicator.updateStage('syncing', ['validating', 'categorizing', 'saving']);

            try {
              const accessToken = await this.getAccessToken();
              const apiPromises = transactionTemplates.map(async (transaction) => {
                const result = await createTransaction(transaction, accessToken);
                // Return the complete transaction with ID and API timestamp
                return {
                  ...transaction,
                  id: result.data.transaction.SK,
                  date: new Date(result.data.transaction.createdAt)
                };
              });
              const completedTransactions = await Promise.all(apiPromises);

              // Add all completed transactions to the array
              this.budgetData.addTransaction(...completedTransactions);

              console.log(`Successfully synced ${completedTransactions.length} split transactions to API`);
              this.loadingIndicator.completeStage('syncing');
            } catch (error) {
              console.error('Failed to sync split transactions to API:', error);
              // Show error modal for API failures
              this.loadingIndicator.hideLoading();
              this.showApiError(error);
              // Fall back to adding transactions without IDs for offline functionality
              this.budgetData.addTransaction(...transactionTemplates);
              return;
            }
          } else {
            // Add transactions without syncing when offline
            this.budgetData.addTransaction(...transactionTemplates);
            this.loadingIndicator.completeStage('saving');
          }

          // Hide loading modal
          this.loadingIndicator.hideLoading();

          this.showAlert('success', 'Transactions Created', `Created ${this.splitTransaction.days} split transactions of $${amountPerDay.toFixed(2)} each`);
        } else {
          // Create single transaction template
          const transactionTemplate = {
            description: cleanedDescription,
            amount: parseFloat(this.newTransaction.amount),
            type: this.newTransaction.type,
            date: transactionDate,
            category: finalCategory
          };

          // Call API first, then add complete transaction to local array
          if (this.user) {
            this.loadingIndicator.completeStage('saving');
            this.loadingIndicator.updateStage('syncing', ['validating', 'categorizing', 'saving']);

            try {
              const accessToken = await this.getAccessToken();
              const result = await createTransaction(transactionTemplate, accessToken);

              // Add the complete transaction with ID and API timestamp
              const completedTransaction = {
                ...transactionTemplate,
                id: result.data.transaction.SK,
                date: new Date(result.data.transaction.createdAt)
              };

              this.budgetData.addTransaction(completedTransaction);

              console.log('Successfully synced transaction to API');
              this.loadingIndicator.completeStage('syncing');
            } catch (error) {
              console.error('Failed to sync transaction to API:', error);
              // Show error modal for API failures
              this.loadingIndicator.hideLoading();
              this.showApiError(error);
              // Fall back to adding transaction without ID for offline functionality
              this.budgetData.addTransaction(transactionTemplate);
              return;
            }
          } else {
            // Add transaction without ID when offline
            this.budgetData.addTransaction(transactionTemplate);
            this.loadingIndicator.completeStage('saving');
          }

          // Hide loading modal
          this.loadingIndicator.hideLoading();

          // Show confetti animation
          this.showConfetti();

          // Show toast notification for single transaction
          if (this.$refs.transactionToast) {
            // Calculate remaining to spend after this transaction (with rollover)
            const updatedRemaining = this.adjustedRemainingBudget;

            this.$refs.transactionToast.show(
              this.newTransaction.type,
              this.newTransaction.amount,
              cleanedDescription,
              updatedRemaining,
              wasAutoCategorized ? finalCategory : '',
              learnedKeywords.count > 0 ? learnedKeywords : null
            );
          }
        }

        // Reset form
        this.newTransaction = {
          description: '',
          amount: null,
          type: 'debit',
          date: new Date(),
          category: ''
        };
        this.lastParsedDescription = '';

        // Clear contenteditable div and blur focus (iOS-compatible)
        if (this.$refs.descriptionInput) {
          this.$refs.descriptionInput.innerHTML = '';
          this.$refs.descriptionInput.blur();

          // iOS workaround: focus on body or another element to force blur
          if (document.activeElement === this.$refs.descriptionInput) {
            // Create a temporary button to steal focus
            const tempButton = document.createElement('button');
            tempButton.style.position = 'absolute';
            tempButton.style.opacity = '0';
            tempButton.style.pointerEvents = 'none';
            document.body.appendChild(tempButton);
            tempButton.focus();
            setTimeout(() => {
              tempButton.blur();
              document.body.removeChild(tempButton);
            }, 100);
          }
        }

        this.setDefaultDateInput();
        this.splitTransaction.enabled = false;
        this.splitTransaction.days = 2;

        this.saveToLocalStorage();
      } catch (error) {
        console.error('Unexpected error in addTransaction:', error);
        this.loadingIndicator.hideLoading();
        this.showApiError(error);
      } finally {
        // Always reset loading state
        this.isAddingTransaction = false;
      }
    },

    // Handle natural language transaction processing
    handleNaturalLanguageTransaction(data) {
      console.log('Natural language transaction received:', data);
    },

    // Handle confirmed transactions from natural language processing
    async handleConfirmedTransactions(data) {
      console.log('Confirmed transactions received:', data);

      if (!data.parsedTransactions || data.parsedTransactions.length === 0) {
        this.showAlert('warning', 'No Transactions', 'No transactions were found to add.');
        return;
      }
      this.isAddingTransaction = true;

      try {
        const transactionsToAdd = [];
        let successCount = 0;
        let failureCount = 0;

        // Process each parsed transaction
        for (const parsedTransaction of data.parsedTransactions) {
          try {
            // Convert parsed transaction to our transaction format
            // Use the parsed date but set time to current moment to capture exact creation time
            const parsedDate = parsedTransaction.date; // YYYY-MM-DD format from API
            const now = new Date();
            
            // Create date with parsed date but current time
            const transactionDate = parsedDate.match(/^\d{4}-\d{2}-\d{2}$/) 
              ? new Date(`${parsedDate}T${now.toTimeString().split(' ')[0]}`) // Use current time
              : this.createDateFromString(parsedDate); // Fallback for other formats
            
            const transactionTemplate = {
              description: parsedTransaction.description,
              amount: parseFloat(parsedTransaction.amount),
              type: parsedTransaction.type,
              date: transactionDate,
              category: parsedTransaction.category || ''
            };

            // Prioritize local category rules over API category
            if (transactionTemplate.description) {
              const localCategory = this.autoCategorize(transactionTemplate.description);
              if (localCategory) {
                // Override API category with local rule
                transactionTemplate.category = localCategory;
              } else if (!transactionTemplate.category) {
                // Only use empty category if no local rule and no API category
                transactionTemplate.category = '';
              }
              // If no local rule but API provided category, keep the API category
            }

            // Learn from category if keyword learning is enabled
            if (this.keywordLearningEnabled && transactionTemplate.category && transactionTemplate.description) {
              try {
                const accessToken = await this.getAccessToken();
                await this.learnFromDescription(transactionTemplate.description, transactionTemplate.category, accessToken);
              } catch (error) {
                console.error('Failed to learn keywords:', error);
                // Continue without learning if API fails
              }
            }

            // Call API to create transaction if user is authenticated
            if (this.user) {
              try {
                const accessToken = await this.getAccessToken();
                const result = await createTransaction(transactionTemplate, accessToken);

                // Add the complete transaction with ID and API timestamp
                const completedTransaction = {
                  ...transactionTemplate,
                  id: result.data.transaction.SK,
                  date: new Date(result.data.transaction.createdAt)
                };

                transactionsToAdd.push(completedTransaction);
                successCount++;
              } catch (error) {
                console.error('Failed to sync transaction to API:', error);
                // Fall back to adding transaction without ID for offline functionality
                transactionsToAdd.push(transactionTemplate);
                successCount++;
              }
            } else {
              // Add transaction without ID when offline
              transactionsToAdd.push(transactionTemplate);
              successCount++;
            }

          } catch (error) {
            console.error('Error processing transaction:', error);
            failureCount++;
          }
        }

        // Add all successful transactions to the global list
        if (transactionsToAdd.length > 0) {
          // Add each transaction individually
          transactionsToAdd.forEach(transaction => {
            this.budgetData.addTransaction(transaction);
          });

          // Show success toast for the first transaction (or summary if multiple)
          if (this.$refs.transactionToast) {
            const firstTransaction = transactionsToAdd[0];
            const updatedRemaining = this.adjustedRemainingBudget;
            
            if (transactionsToAdd.length === 1) {
              // Single transaction - show full details
              this.$refs.transactionToast.show(
                firstTransaction.type,
                firstTransaction.amount,
                firstTransaction.description,
                updatedRemaining,
                firstTransaction.category,
                null // No keyword learning info here
              );
            } else {
              // Multiple transactions - show summary
              this.$refs.transactionToast.show(
                'debit',
                0,
                '',
                updatedRemaining,
                '',
                null,
                `${transactionsToAdd.length} Transactions Added`
              );
            }
          }
        } else {
          this.$refs.transactionToast.hide();
          this.showAlert('error', 'Processing Failed', 'Failed to process any transactions. Please try again.');
        }
      } catch (error) {
        console.error('Error handling confirmed transactions:', error);
        this.$refs.transactionToast.hide();
        this.showApiError(error);
      } finally {
        this.isAddingTransaction = false;
      }
    },

    // Handle processing started event - show toast notification
    handleProcessingStarted(data) {
      console.log('Processing started:', data);
      // Show loading toast with "Processing..." message
      this.$refs.transactionToast.showLoading(1, {
        type: 'debit',
        amount: 0,
        description: data.description
      });
    },

    // Handle transaction processed event from header modal
    handleTransactionProcessed(data) {
      // This is called when the natural language transaction is processed but not yet confirmed
      // We can use this for preview or logging purposes
      console.log('Transaction processed from header modal:', data);
      
      // Hide the loading toast when processing completes (with small delay for UX)
      setTimeout(() => {
        if (this.$refs.transactionToast && this.$refs.transactionToast.isLoading) {
          // this.$refs.transactionToast.hide();
        }
      }, 300);
    },

    // Handle transactions confirmed event from header modal
    handleTransactionsConfirmed(data) {
      // This is called when the user confirms the transactions from the header modal
      // Delegate to the existing method
      this.handleConfirmedTransactions(data);
    },

    // Handle savings contribution event from SavingsGoals component
    async handleSavingsContribution(transaction) {
      try {
        // The SavingsGoals component has already created the transaction and updated the goal
        // We need to refresh our local transaction data to reflect the new savings transaction
        console.log('Savings contribution made:', transaction);
        
        this.budgetData.addTransaction(transaction);
        
        // Show a toast notification about the savings contribution
        if (this.$refs.transactionToast) {
          const updatedRemaining = this.adjustedRemainingBudget;
          this.$refs.transactionToast.show(
            'savings', // Use savings type for proper toast styling
            transaction.amount,
            transaction.description,
            updatedRemaining,
            'Savings', // Category
            null // No keyword learning for savings contributions
          );
        }
        
        console.log('Successfully processed savings contribution and updated local state');
      } catch (error) {
        console.error('Failed to process savings contribution:', error);
        // The contribution was already made, so we don't want to show an error that might confuse the user
        // Just log it for debugging purposes
      }
    },

    formatDateCsv(date) {
      return new Date(date).toLocaleDateString();
    },

    // Format date
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // Format date for table display (shorter version)
    formatTableDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // Format date primary (day and month)
    formatTableDatePrimary(date) {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    },

    // Format date secondary (time)
    formatTableDateSecondary(date) {
      return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // Format day header for grouped transactions
    formatDayHeader(date) {
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      const targetDate = new Date(date);

      // Check if it's today
      if (targetDate.toDateString() === today.toDateString()) {
        return 'Today - ' + targetDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        });
      }

      // Check if it's yesterday
      if (targetDate.toDateString() === yesterday.toDateString()) {
        return 'Yesterday - ' + targetDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        });
      }

      // For other dates, show full date with weekday
      return targetDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    },

    // Trigger file input click
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    // Import transactions from CSV file
    importFromCSV(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvData = e.target.result;
          const lines = csvData.split('\n');

          // Skip header row
          if (lines.length < 2) {
            this.showAlert('error', 'Invalid File', 'Invalid CSV file format');
            return;
          }

          // Find column indices from header
          const headerLine = lines[0].trim();
          const headers = this.parseCSVLine(headerLine);

          const dateIndex = headers.findIndex(h => h.toLowerCase() === 'date');
          const merchantIndex = headers.findIndex(h => h.toLowerCase() === 'merchant');
          const amountIndex = headers.findIndex(h => h.toLowerCase() === 'amount');

          if (dateIndex === -1 || merchantIndex === -1 || amountIndex === -1) {
            this.showAlert('error', 'Missing Columns', 'CSV file must contain Date, Merchant, and Amount columns');
            return;
          }

          // Process each line (skip header)
          const importedTransactions = [];
          let successCount = 0;

          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue; // Skip empty lines

            // Parse CSV line (handle quoted values with commas)
            const values = this.parseCSVLine(line);

            // Skip if we don't have enough values
            if (values.length <= Math.max(dateIndex, merchantIndex, amountIndex)) {
              continue;
            }

            // Parse date
            let date;
            try {
              date = new Date(`${values[dateIndex]}T00:00:00`);
              if (isNaN(date.getTime())) {
                // Skip invalid dates
                continue;
              }
            } catch (e) {
              continue;
            }

            // Parse amount and determine transaction type
            const amountStr = values[amountIndex].replace(/[^0-9.-]/g, '');
            const amount = parseFloat(amountStr);

            if (isNaN(amount)) continue;

            // Determine type based on amount sign
            // Positive amount = credit (income), Negative amount = debit (expense)
            const type = amount >= 0 ? 'credit' : 'debit';

            // Use absolute value for the amount
            const absoluteAmount = Math.abs(amount);

            // Create transaction object
            const transaction = {
              date: date,
              description: values[merchantIndex].trim(),
              type: type,
              amount: absoluteAmount
            };

            importedTransactions.push(transaction);
            successCount++;
          }

          // Add imported transactions to the existing ones
          if (importedTransactions.length > 0) {
            // Add to transactions array
            this.budgetData.addTransaction(...importedTransactions);

            this.showAlert('success', 'Transactions Imported', `Successfully imported ${successCount} transactions.`);
          } else {
            this.showAlert('warning', 'No Transactions', 'No valid transactions found in the CSV file.');
          }
        } catch (error) {
          console.error('Error importing CSV:', error);
          this.showApiError(error);
        }

        // Reset file input
        event.target.value = '';
      };

      reader.readAsText(file);
    },

    // Helper method to parse CSV lines (handles quoted values with commas)
    parseCSVLine(line) {
      const values = [];
      let inQuotes = false;
      let currentValue = '';

      for (let j = 0; j < line.length; j++) {
        const char = line[j];

        if (char === '"' && (j === 0 || line[j - 1] !== '\\')) {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(currentValue);
          currentValue = '';
        } else {
          currentValue += char;
        }
      }

      // Add the last value
      values.push(currentValue);

      return values;
    },

    // Export transactions to CSV
    async exportToCSV() {
      if (this.currentMonthTransactions.length === 0) {
        this.showAlert('warning', 'No Transactions', 'No current month transactions to export');
        return;
      }

      // CSV header
      let csvContent = 'Date,Merchant,Amount\n';

      // Add transaction data
      this.currentMonthTransactions.forEach(transaction => {
        const date = this.formatDateCsv(transaction.date);
        const description = transaction.description.replace(/,/g, ' '); // Remove commas to avoid CSV issues
        const amount = transaction.type === 'debit' ? (transaction.amount * -1).toFixed(2) : transaction.amount;

        csvContent += `"${date}",${description},${amount}\n`;
      });

      try {
        // Import Capacitor plugins
        const { Filesystem, Directory } = await import('@capacitor/filesystem');

        const fileName = `budget-transactions-${new Date().toISOString().slice(0, 10)}.csv`;

        if (Capacitor.isNativePlatform()) {
          // On native platforms (Android/iOS), use Filesystem API
          try {
            // Write the file to cache directory first
            const result = await Filesystem.writeFile({
              path: `vue-budget/${fileName}`,
              data: csvContent,
              directory: Directory.Documents,
              encoding: 'utf8',
              recursive: true
            });
            this.showAlert('success', 'File Saved', `File saved to Downloads as ${result.uri}`);
          } catch (e) {
            console.error('Error saving file:', e);
            this.showApiError(e);
          }
        } else {
          // On web, use the traditional approach
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);

          link.setAttribute('href', url);
          link.setAttribute('download', fileName);
          link.style.visibility = 'hidden';

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (error) {
        console.error('Error exporting CSV:', error);
        this.showApiError(error);
      }
    },

    // Delete transaction
    deleteTransaction(transaction) {
      this.pendingDeleteTransaction = transaction;
      this.pendingConfirmAction = 'deleteTransaction';
      this.showAlert('confirm', 'Delete Transaction', 'Are you sure you want to delete this transaction? This action cannot be undone.');
    },

    // Actually delete the transaction after confirmation
    async performDeleteTransaction() {
      if (this.pendingDeleteTransaction !== null) {
        const transaction = this.pendingDeleteTransaction;

        // Call API to delete transaction if user is authenticated and transaction has an ID
        if (this.user && transaction.id) {
          try {
            const accessToken = await this.getAccessToken();
            await deleteTransactionAPI(transaction.id, accessToken);
            this.loadingIndicator.completeStage('deleting');
            this.loadingIndicator.hideWithDelay(2000);
            console.log('Successfully deleted transaction from API');
          } catch (error) {
            console.error('Failed to delete transaction from API:', error);
            this.showApiError(error);
            this.pendingDeleteTransaction = null;
            this.pendingConfirmAction = null;
            return;
          }
        }

        // Remove from local array using the new composable methods
        let deleted = false;
        if (transaction.id) {
          deleted = this.budgetData.removeTransactionById(transaction.id);
        }

        if (!deleted) {
          console.error('Failed to find and delete transaction');
          this.showAlert('error', 'Delete Failed', 'Could not find the transaction to delete.');
        }

        this.pendingDeleteTransaction = null;
        this.pendingConfirmAction = null;
      }
    },

    // Start editing a transaction
    startEditTransaction(transaction) {
      this.editingTransaction = transaction;
      this.editingTransactionData = {
        description: transaction.description,
        amount: transaction.amount,
        category: transaction.category || ''
      };
    },

    // Cancel transaction edit
    cancelTransactionEdit() {
      this.editingTransaction = null;
      this.editingTransactionData = {
        description: '',
        amount: null,
        category: ''
      };
    },

    // Save transaction edit
    async saveTransactionEdit() {
      if (!this.editingTransaction || !this.editingTransactionData.description || !this.editingTransactionData.amount || this.editingTransactionData.amount <= 0) {
        return;
      }

      const transaction = this.editingTransaction;
      const updates = {
        description: this.editingTransactionData.description,
        amount: this.editingTransactionData.amount
      };

      // Include category if provided
      if (this.editingTransactionData.category) {
        updates.category = this.editingTransactionData.category;
      }

      // Show loading indicator
      this.loadingIndicator.showLoading({
        title: 'Updating Transaction',
        subtitle: 'Saving changes...',
        currentStage: 'updating',
        completedStages: [],
        stages: [{
          key: 'updating',
          title: 'Saving Changes',
          description: 'Updating transaction details...',
          icon: 'fa-solid fa-save'
        }]
      });

      try {
        // Update via API if user is authenticated and transaction has an ID
        if (this.user && transaction.id) {
          const accessToken = await this.getAccessToken();
          await updateTransactionAPI(transaction.id, updates, accessToken);
          this.loadingIndicator.completeStage('updating');
          console.log('Successfully updated transaction via API');
        }

        // Update local transaction
        transaction.description = updates.description;
        transaction.amount = updates.amount;
        if (updates.category !== undefined) {
          transaction.category = updates.category;
        }

        // Update in budgetData composable
        if (transaction.id) {
          this.budgetData.updateTransaction(transaction.id, updates);
        }

        // Clear edit state
        this.cancelTransactionEdit();

        // Hide loading indicator
        this.loadingIndicator.hideWithDelay(1000);

        // Show success toast
        if (this.$refs.transactionToast) {
          this.$refs.transactionToast.show(
            transaction.type,
            transaction.amount,
            transaction.description,
            this.todayRemainingBudget,
            transaction.category,
            null,
            'Transaction Updated'
          );
        }
      } catch (error) {
        console.error('Failed to update transaction:', error);
        this.loadingIndicator.hideLoading();
        this.showApiError(error);
      }
    },

    saveToLocalStorage() {
      // legacy storage
    },

    // Load all data from API (budget, transactions, user config, and keywords)
    async loadFromAPI() {
      if (!this.user) {
        return { budgetLoaded: false, transactionsLoaded: false, configLoaded: false, keywordsLoaded: false, savingsGoalsLoaded: false };
      }

      const results = { budgetLoaded: false, transactionsLoaded: false, configLoaded: false, keywordsLoaded: false, savingsGoalsLoaded: false };

      try {
        const accessToken = await this.getAccessToken();

        // Stage 1: Load user configuration data
        this.loadingStage = 'config';
        try {
          const configResult = await getUserConfig(accessToken);
          const configData = configResult.data.config;

          // Update app state with config from API
          this.isConfigured = configData.isConfigured;
          this.showOnboarding = false;
          this.showNaturalLanguageSetup = configData.isConfigured ? false : true;
          this.savingsGoal = configData.savingsGoal;
          this.keywordLearningEnabled = configData.features.keywordLearningEnabled;
          this.aiCategorizationEnabled = configData.features.aiCategorizationEnabled;

          // Load budget start date if available (Requirement 12.2, 12.3)
          if (configData.budgetStartDate) {
            this.budgetData.setBudgetStartDate(configData.budgetStartDate);
          }

          console.log('User config loaded from API:', {
            isConfigured: configData.isConfigured,
            showOnboarding: false,
            showNaturalLanguageSetup: configData.isConfigured ? false : true,
            savingsGoal: configData.savingsGoal,
            budgetStartDate: configData.budgetStartDate
          });
          this.budgetData.updateSavingsGoal(this.savingsGoal);
          results.configLoaded = true;

          // Small delay to show progress
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          console.error('Failed to load user config from API:', error);
          this.showApiError(error);
        }

        // Stage 2: Load budget data
        this.loadingStage = 'budget';
        this.completedStages.push('config');
        try {
          const budgetResult = await getBudget(accessToken);
          const budgetData = budgetResult.data;

          // Separate income and expenses
          const incomeItems = budgetData.items.filter(item => item.kind === 'income');
          const expenseItems = budgetData.items.filter(item => item.kind === 'expense');

          // Calculate gross monthly income from all income sources
          this.grossMonthlyIncome = incomeItems.reduce((total, item) => total + item.amount, 0);
          this.budgetData.updateGrossIncome(this.grossMonthlyIncome);

          // Transform expenses to match existing format
          this.monthlyExpenses = expenseItems.map(item => ({
            name: item.SK.replace('BUDGET#CATEGORY#', ''), // Extract category name from SK
            amount: item.amount
          }));
          this.budgetData.updateMonthlyExpenses(this.monthlyExpenses);

          console.log('Budget loaded from API:', budgetData.summary);
          results.budgetLoaded = true;

          // Small delay to show progress
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          console.error('Failed to load budget from API:', error);
          this.showApiError(error);
        }

        // Stage 3: Load savings goals data (Requirement 8.3, 8.4)
        // Load goals BEFORE transactions so we know which months to load
        this.loadingStage = 'savings-goals';
        this.completedStages.push('budget');
        try {
          // Load savings goals using the composable
          await this.savingsGoals.loadGoals();
          console.log(`Loaded ${this.savingsGoals.goals.value.length} savings goals from API`);
          results.savingsGoalsLoaded = true;

          // Small delay to show progress
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          console.error('Failed to load savings goals from API:', error);
          this.showApiError(error);
        }

        // Stage 4: Load transactions data
        // Now that goals are loaded, we can determine which months to load
        this.loadingStage = 'transactions';
        this.completedStages.push('savings-goals');
        try {
          const currentDate = new Date();
          const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
          
          // Determine which months we need to load transactions for
          const monthsToLoad = new Set([currentMonth]);
          
          // Add months for active savings goals if we have goals loaded
          if (results.savingsGoalsLoaded && this.savingsGoals.goals.value.length > 0) {
            this.savingsGoals.goals.value.forEach(goal => {
              if (goal.createdDate) {
                const goalStartDate = new Date(goal.createdDate);
                const goalEndDate = new Date(goal.createdDate);
                goalEndDate.setDate(goalEndDate.getDate() + goal.duration + (goal.totalPausedDays || 0));
                
                // Add all months from goal start to goal end (or current month, whichever is later)
                let monthIterator = new Date(goalStartDate.getFullYear(), goalStartDate.getMonth(), 1);
                const endMonth = goalEndDate > currentDate ? currentDate : goalEndDate;
                
                while (monthIterator <= endMonth) {
                  const monthKey = `${monthIterator.getFullYear()}-${String(monthIterator.getMonth() + 1).padStart(2, '0')}`;
                  monthsToLoad.add(monthKey);
                  monthIterator.setMonth(monthIterator.getMonth() + 1);
                }
              }
            });
          }

          // Load transactions for all required months
          const allTransactions = [];
          for (const month of monthsToLoad) {
            try {
              const monthTransactionsResult = await getTransactions(month, accessToken);
              allTransactions.push(...monthTransactionsResult.data.transactions);
            } catch (error) {
              console.warn(`Failed to load transactions for month ${month}:`, error);
              // Continue loading other months even if one fails
            }
          }

          const transactionCount = this.budgetData.setTransactions(allTransactions);

          console.log(`Loaded ${this.budgetData.transactions.value.length} transactions from API for months: ${Array.from(monthsToLoad).join(', ')}`);
          results.transactionsLoaded = true;

          // Small delay to show progress
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          console.error('Failed to load transactions from API:', error);
          this.showApiError(error);
        }

        // Stage 5: Load keyword mappings data
        this.loadingStage = 'keywords';
        this.completedStages.push('transactions');
        try {
          const keywordsResult = await getKeywordMappings(accessToken);
          console.log(`Loaded ${keywordsResult.data.count} keyword mappings from API`);
          results.keywordsLoaded = true;
          this.completedStages.push('keywords');

          // Small delay to show progress
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
          console.error('Failed to load keywords from API:', error);
          this.showApiError(error);
        }

      } catch (error) {
        console.error('Failed to get access token:', error);
        this.showApiError(error);
      }

      return results;
    },

    // Save user configuration data to API
    async saveUserConfigToAPI() {
      this.loadingIndicator.showLoading({
        title: 'Saving',
        subtitle: 'Saving user configuration',
        currentStage: 'saving',
        stages: [{
          key: 'saving',
          title: 'Saving Configuration',
          description: 'Saving user configuration to the cloud...',
          icon: 'fa-solid fa-cloud-upload-alt'
        }]
      });
      
      if (!this.user) {
        return false;
      }

      try {
        const accessToken = await this.getAccessToken();

        // Create config update request with current app state
        const configUpdate = {
          isConfigured: this.isConfigured,
          showOnboarding: this.showOnboarding,
          showNaturalLanguageSetup: this.showNaturalLanguageSetup,
          savingsGoal: this.budgetData.savingsGoal.value,
          features: {
            keywordLearningEnabled: this.keywordLearningEnabled,
            aiCategorizationEnabled: this.aiCategorizationEnabled
          }
        };

        const result = await updateUserConfig(configUpdate, accessToken);
        console.log('User config saved to API:', result.data.config);
        this.loadingIndicator.completeStage('saving');
        this.loadingIndicator.hideWithDelay(1500);
        return true;
      } catch (error) {
        console.error('Failed to save user config to API:', error);
        this.showApiError(error);
        return false;
      }
    },

    // Load budget start date from API (Requirement 12.2, 12.3, 12.4)
    async loadBudgetStartDate() {
      try {
        const accessToken = await this.getAccessToken();
        const configResult = await getUserConfig(accessToken);
        
        if (configResult.data && configResult.data.config && configResult.data.config.budgetStartDate) {
          this.budgetData.setBudgetStartDate(configResult.data.config.budgetStartDate);
          console.log('Budget start date loaded from API:', configResult.data.config.budgetStartDate);
        }
      } catch (error) {
        console.error('Error loading budget start date:', error);
        // Non-critical error - app can continue without it
      }
    },

    // Save budget start date to API (Requirement 12.1, 12.5)
    async saveBudgetStartDate(budgetStartDate) {
      try {
        const accessToken = await this.getAccessToken();
        await updateUserConfig({ budgetStartDate }, accessToken);
        console.log('Budget start date saved to API:', budgetStartDate);
      } catch (error) {
        console.error('Error saving budget start date:', error);
        this.showApiError(error);
        // Set budgetStartDate locally even if API fails
        this.budgetData.setBudgetStartDate(budgetStartDate);
      }
    },

    // Save budget data to API
    async saveBudgetToAPI() {
      try {
        const accessToken = await this.getAccessToken();

        // Transform current budget data to API format
        const budgetItems = [];

        // Add income items
        if (this.grossMonthlyIncome > 0) {
          budgetItems.push({
            name: 'salary',
            amount: this.grossMonthlyIncome,
            kind: 'income',
            currency: 'USD'
          });
        }

        // Add expense items
        this.monthlyExpenses.forEach(expense => {
          budgetItems.push({
            name: expense.name,
            amount: expense.amount,
            kind: 'expense',
            currency: 'USD'
          });
        });

        if (budgetItems.length > 0) {
          const result = await storeBudget(budgetItems, accessToken);
          console.log('Budget saved to API:', result.data.summary);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Failed to save budget to API:', error);
        this.showApiError(error);
        return false;
      }
    },

    // Load data from localStorage and API
    async loadFromStorage() {
      // Load data from API if user is authenticated
      if (this.user) {
        // Reset progress tracking
        this.loadingStage = 'config';
        this.completedStages = [];
        this.apiLoaded = false;

        await this.loadFromAPI();
        this.isLoading = false;
        this.isLoadingTransactions = false;
        this.apiLoaded = true;
        this.showConfetti();
      }
    },

    // Load transactions for a specific month
    // Also loads historical transactions for active savings goals
    async loadTransactionsForMonth(date) {
      if (!this.user) return;

      this.isLoadingTransactions = true;

      try {
        const accessToken = await this.getAccessToken();
        const targetMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        // Determine which months we need to load transactions for
        const monthsToLoad = new Set([targetMonth]);
        
        // Add months for active savings goals
        if (this.savingsGoals.goals.value.length > 0) {
          const currentDate = new Date();
          this.savingsGoals.goals.value.forEach(goal => {
            if (goal.createdDate) {
              const goalStartDate = new Date(goal.createdDate);
              const goalEndDate = new Date(goal.createdDate);
              goalEndDate.setDate(goalEndDate.getDate() + goal.duration + (goal.totalPausedDays || 0));
              
              // Add all months from goal start to goal end (or current month, whichever is later)
              let monthIterator = new Date(goalStartDate.getFullYear(), goalStartDate.getMonth(), 1);
              const endMonth = goalEndDate > currentDate ? currentDate : goalEndDate;
              
              while (monthIterator <= endMonth) {
                const monthKey = `${monthIterator.getFullYear()}-${String(monthIterator.getMonth() + 1).padStart(2, '0')}`;
                monthsToLoad.add(monthKey);
                monthIterator.setMonth(monthIterator.getMonth() + 1);
              }
            }
          });
        }
        
        // Load transactions for all required months
        const allTransactions = [];
        for (const month of monthsToLoad) {
          try {
            const monthTransactionsResult = await getTransactions(month, accessToken);
            allTransactions.push(...monthTransactionsResult.data.transactions);
          } catch (error) {
            console.warn(`Failed to load transactions for month ${month}:`, error);
            // Continue loading other months even if one fails
          }
        }
        
        // Replace transactions with all loaded data
        this.budgetData.setTransactions(allTransactions);
        
        console.log(`Loaded ${allTransactions.length} transactions for months: ${Array.from(monthsToLoad).join(', ')}`);
      } catch (error) {
        console.error('Failed to load transactions for month:', error);
        this.showApiError(error);
      } finally {
        this.isLoadingTransactions = false;
      }
    },

    // Add monthly expense
    addMonthlyExpense(expense) {
      // Handle both direct calls (from setup modal) and component events
      if (expense && typeof expense === 'object' && expense.name && expense.amount) {
        this.monthlyExpenses.push({
          name: expense.name,
          amount: parseFloat(expense.amount)
        });
        this.saveToLocalStorage();
      } else if (this.newExpense.name && this.newExpense.amount) {
        this.monthlyExpenses.push({
          name: this.newExpense.name,
          amount: parseFloat(this.newExpense.amount)
        });
        this.newExpense = {
          name: '',
          amount: null
        };
        this.saveToLocalStorage();
      } else {
        this.showAlert('warning', 'Missing Information', 'Please fill in all fields');
      }
    },

    // Remove monthly expense
    removeMonthlyExpense(index) {
      this.monthlyExpenses.splice(index, 1);
      this.saveToLocalStorage();
    },

    editBudgetSetup() {
      this.isEditingBudget = true;
    },

    async cancelEdit() {
      this.isEditingBudget = false;
      // Reload data from localStorage to revert any unsaved changes
      await this.loadFromStorage();
    },

    handleKeyDown(event) {
      if (event.key === 'Escape') {
        if (this.showAlertModal) {
          if (this.alertType === 'confirm') {
            this.cancelConfirmation();
          } else {
            this.closeAlertModal();
          }
        } else if (this.isEditingBudget) {
          this.cancelEdit();
        }
      }
    },

    showAlert(type, title, message) {
      this.alertType = type;
      this.alertTitle = title;
      this.alertMessage = message;
      this.showAlertModal = true;
    },

    closeAlertModal() {
      this.showAlertModal = false;
    },

    cancelConfirmation() {
      this.pendingDeleteIndex = null;
      this.pendingConfirmAction = null;
      this.closeAlertModal();
    },

    confirmAction() {
      if (this.pendingConfirmAction === 'deleteTransaction') {
        this.closeAlertModal();
        this.loadingIndicator.showLoading({
          title: 'Deleting',
          subtitle: 'Deleting the transaction',
          currentStage: 'deleting',
          stages: [{
            key: 'deleting',
            title: 'Deleting Transaction',
            description: 'Deleting transaction from the cloud...',
            icon: 'fa-solid fa-cloud-upload-alt'
          }]
        })
        this.performDeleteTransaction();
        this.pendingDeleteIndex = null;
        this.pendingConfirmAction = null;
      } else if (this.pendingConfirmAction === 'clearTransactions') {
        this.resetApp();
        this.closeAlertModal();
      }
      // Add other confirmation actions here in the future if needed
    },

    confirmResetApp() {
      this.pendingConfirmAction = 'clearTransactions';
      this.showAlert('confirm', 'Clear All Transactions', 'Are you sure you want to clear all transactions? Your budget settings and monthly expenses will be preserved. This action cannot be undone.');
    },

    resetApp() {
      // Only clear transactions and reset transaction-related form states
      this.transactionDateInput = '';
      this.newTransaction = {
        description: '',
        amount: null,
        type: 'debit',
        date: new Date(),
        category: ''
      };
      this.splitTransaction = {
        enabled: false,
        days: 2
      };
      this.saveToLocalStorage();
    },

    showConfetti() {
      const canvas = document.createElement('canvas');
      canvas.style.position = 'fixed';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '9999';
      document.body.appendChild(canvas);

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const ctx = canvas.getContext('2d');

      const confetti = [];
      const colors = ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'];
      const confettiCount = 150;

      for (let i = 0; i < confettiCount; i++) {
        confetti.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height - canvas.height,
          r: Math.random() * 6 + 4,
          d: Math.random() * confettiCount,
          color: colors[Math.floor(Math.random() * colors.length)],
          tilt: Math.floor(Math.random() * 10) - 10,
          tiltAngleIncremental: Math.random() * 0.07 + 0.05,
          tiltAngle: 0
        });
      }

      let animationFrame;
      let startTime = Date.now();
      const duration = 3000;
      const fadeStart = 2000;

      const draw = () => {
        const elapsed = Date.now() - startTime;
        const opacity = elapsed > fadeStart ? 1 - (elapsed - fadeStart) / (duration - fadeStart) : 1;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.globalAlpha = opacity;

        confetti.forEach((c, i) => {
          ctx.beginPath();
          ctx.lineWidth = c.r / 2;
          ctx.strokeStyle = c.color;
          ctx.moveTo(c.x + c.tilt + c.r / 4, c.y);
          ctx.lineTo(c.x + c.tilt, c.y + c.tilt + c.r / 4);
          ctx.stroke();

          c.tiltAngle += c.tiltAngleIncremental;
          c.y += (Math.cos(c.d) + 3 + c.r / 2) / 2;
          c.x += Math.sin(c.d);
          c.tilt = Math.sin(c.tiltAngle - i / 3) * 15;

          if (c.y > canvas.height) {
            confetti[i] = {
              x: Math.random() * canvas.width,
              y: -10,
              r: c.r,
              d: c.d,
              color: c.color,
              tilt: c.tilt,
              tiltAngleIncremental: c.tiltAngleIncremental,
              tiltAngle: c.tiltAngle
            };
          }
        });

        if (elapsed < duration) {
          animationFrame = requestAnimationFrame(draw);
        } else {
          document.body.removeChild(canvas);
        }
      };
      draw();
    }
  },
  async mounted() {
    // Initialize basic UI elements
    this.setDefaultDateInput();
    this.filteredTransactions = [];
    document.addEventListener('keydown', this.handleKeyDown);

    // Add window resize listener for responsive behavior
    window.addEventListener('resize', this.handleResize);

    // Open side menu by default on desktop
    this.checkDesktopView();
    window.addEventListener('resize', this.checkDesktopView);

    // Re-initialize swipe gesture with proper context (since setup() runs before methods are available)
    const { useSwipeGesture } = await import('./composables/useSwipeGesture.js');
    this.swipeGesture = useSwipeGesture({
      swipeThreshold: 50,
      minVelocity: 0.3,
      onSwipeLeft: () => this.navigateToNextCategory(),
      onSwipeRight: () => this.navigateToPreviousCategory()
    });

    // Update navigation bar color for native platforms
    if (Capacitor.isNativePlatform()) {
      if (this.isDarkMode) {
        NavigationBar.setNavigationBarColor({ color: NavigationBarColor.BLACK, darkButtons: false });
      } else {
        NavigationBar.setNavigationBarColor({ color: NavigationBarColor.WHITE, darkButtons: true });
      }
    }
  },
  beforeUnmount() {
    // Remove ESC key listener
    document.removeEventListener('keydown', this.handleKeyDown);
    // Remove resize listener
    window.removeEventListener('resize', this.handleResize);
    // Remove desktop view check listener
    window.removeEventListener('resize', this.checkDesktopView);
  },
  watch: {
    // Watch for authentication state changes
    user: {
      handler(newUser, oldUser) {
        // Only load data on initial auth state determination or auth state changes
        if (!this.hasLoadedInitialData || (newUser !== oldUser)) {
          this.loadFromStorage();
          this.hasLoadedInitialData = true;
        }
      },
      immediate: true // Run immediately when component is created
    },
    // Watch for month navigation changes
    'budgetData.currentDate.value': {
      async handler(newDate, oldDate) {
        // Only reload if user is authenticated and month actually changed
        if (this.user && oldDate && 
            (newDate.getMonth() !== oldDate.getMonth() || 
             newDate.getFullYear() !== oldDate.getFullYear())) {
              console.log('load');
          await this.loadTransactionsForMonth(newDate);
        }
      }
    },
    // Watch for viewing mode changes to adjust selected category
    'budgetData.isViewingCurrentMonth.value': {
      handler(isViewingCurrent) {
        // If viewing past month and currently on savings or categories, switch to 'all'
        if (!isViewingCurrent && (this.selectedCategory === 'savings' || this.selectedCategory === 'categories')) {
          this.selectedCategory = 'all';
        }
      }
    },
    grossMonthlyIncome() {
      this.saveToLocalStorage();
    },
    monthlyExpenses: {
      handler() {
        this.saveToLocalStorage();
      },
      deep: true
    },
    transactions: {
      handler() {
        this.saveToLocalStorage();
      },
      deep: true
    },
    keywordLearningEnabled() {
      this.saveToLocalStorage();
    },
    isDarkMode(newValue) {
      // Update navigation bar color for native platforms when dark mode changes
      if (Capacitor.isNativePlatform()) {
        if (newValue) {
          NavigationBar.setNavigationBarColor({ color: NavigationBarColor.BLACK, darkButtons: false });
        } else {
          NavigationBar.setNavigationBarColor({ color: NavigationBarColor.WHITE, darkButtons: true });
        }
      }
    }
  }
}
</script>

<style scoped>
/* API Loading Overlay */
.api-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-card);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  text-align: center;
  max-width: 400px;
  padding: 2rem;
}

.loading-spinner .spinner-border {
  width: 4rem;
  height: 4rem;
  border-width: 0.4em;
}

.loading-title {
  color: var(--text-primary);
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.loading-subtitle {
  color: var(--text-secondary);
  margin-bottom: 0;
  font-size: 1rem;
}

/* Component-specific styles */
.card {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color);
  color: var(--text-primary) !important;
  box-shadow: none !important;
  transition: all 0.3s ease;
}

/* Fixed container with side menu push effect */
.fixed-container {
  transition: margin-left 0.3s ease-out, width 0.3s ease-out;
  margin-left: 0;
  width: 100%;
}

/* Desktop: push content when menu opens */
@media (min-width: 769px) {
  .fixed-container.menu-open {
    margin-left: 225px;
    width: calc(100% - 225px);
  }
}

/* Mobile: overlay mode, no push */
@media (max-width: 768px) {
  .fixed-container.menu-open {
    margin-left: 0;
    width: 100%;
  }
}

/* Fade Transition */
.fade-enter-active {
  transition: all 0.8s ease-out;
}

.fade-leave-active {
  transition: all 0.5s ease-in;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.dark-mode .form-label {
  color: var(--text-primary) !important;
}

.dark-mode .form-check-label {
  color: var(--text-primary) !important;
}

.dark-mode .form-text {
  color: var(--text-secondary) !important;
}

.dark-mode .btn-outline-secondary {
  color: #b3b3b3 !important;
  border-color: #555555 !important;
}

.dark-mode .btn-outline-secondary:hover {
  background-color: #555555 !important;
  border-color: #555555 !important;
}

.dark-mode .btn-outline-danger {
  color: #ff6b6b !important;
  border-color: #ff6b6b !important;
}

.dark-mode .btn-outline-danger:hover {
  background-color: #ff6b6b !important;
  border-color: #ff6b6b !important;
}

.search-icon {
  border-width: 2px;
  border-right: none;
}

/* Month Navigator Styles */
.month-navigator-card {
  border-radius: 5px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid var(--border-color);
}

.month-navigator-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.month-nav-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.5rem;
}

.month-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  min-width: 250px;
}

.month-label {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
}

.viewing-past-badge {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: var(--text-secondary);
  background: var(--nested-container-bg);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  border: 1px solid var(--border-color);
}

.nav-btn {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 1.25rem;
  transition: all 0.2s ease;
  border: 2px solid #36A2EB;
  color: #36A2EB;
  background: transparent;
}

.nav-btn:hover:not(:disabled) {
  background: #36A2EB;
  color: white;
  transform: scale(1.05);
}

.nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  border-color: var(--border-color);
  color: var(--text-secondary);
}

.today-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.today-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(54, 162, 235, 0.3);
}

/* Responsive Design for Month Navigator */
@media (max-width: 768px) {
  .month-nav-container {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .month-display {
    min-width: 200px;
    order: -1;
    width: 100%;
  }

  .month-label {
    font-size: 1.1rem;
  }

  .today-btn {
    width: 100%;
    justify-content: center;
    order: 3;
  }

  .nav-btn {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
  }
}

@media (max-width: 576px) {
  .month-label {
    font-size: 1rem;
  }

  .viewing-past-badge {
    font-size: 0.75rem;
    padding: 0.2rem 0.6rem;
  }
}

/* Filter input styling */
.input-group .btn {
  border-left: none;
}

.dark-mode .input-group .btn-outline-secondary {
  background-color: #404040 !important;
  border-color: #555555 !important;
  color: #b3b3b3 !important;
}

.dark-mode .input-group .btn-outline-secondary:hover {
  background-color: #555555 !important;
  color: #ffffff !important;
}

/* Dark mode app header */
.dark-mode .app-header {
  background: #2d3748 !important;
  color: #ffffff !important;
}

.dark-mode .app-header.bg-danger {
  background: #e53e3e !important;
}

.app-header {
  position: relative;
}

.app-header .btn {
  font-size: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.app-header .btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.75);
}

/* Modal Overlay Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
  animation: fadeIn 0.3s ease;
}

.dark-mode .modal-overlay {
  background-color: rgba(0, 0, 0, 0.8);
}

.modal-content {
  background: var(--bg-card);
  color: var(--text-primary);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-hover);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease;
  transition: all 0.3s ease;
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--bg-secondary);
}

.modal-title {
  margin: 0;
  font-weight: 600;
  color: var(--text-primary);
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0.25rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
}

.btn-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: var(--text-primary);
}

.dark-mode .btn-close:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.modal-body {
  padding: 1.5rem;
  max-height: calc(90vh - 140px);
  overflow-y: auto;
  background-color: var(--bg-card);
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  background-color: var(--bg-secondary);
}

.expenses-list {
  max-height: 200px;
  overflow-y: auto;
  background-color: var(--bg-card);
  border-color: var(--border-color) !important;
}

/* Dark mode bg-light elements */
.dark-mode .bg-light {
  background-color: #404040 !important;
  color: var(--text-primary) !important;
}

/* Budget Summary Styles */
.budget-summary {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1rem;
}

.budget-summary h6 {
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-weight: 600;
}

.summary-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 1rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.summary-row.net {
  padding-top: 0.75rem;
}

.summary-label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.summary-row.net .summary-label {
  font-weight: 700;
  font-size: 1rem;
}

.summary-value {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.summary-value.income {
  color: var(--income-color);
}

.summary-value.expense {
  color: var(--expense-color);
}

.summary-value.positive {
  color: var(--light-success);
  font-weight: 700;
  font-size: 1.25rem;
}

.summary-value.negative {
  color: var(--expense-color);
  font-weight: 700;
  font-size: 1.25rem;
}

.summary-row.net .summary-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.divider {
  height: 2px;
  background: var(--light-border);
  margin: 0.5rem 0;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }

  .modal-content {
    max-height: 95vh;
  }

  .modal-header,
  .modal-footer {
    padding: 1rem;
  }

  .modal-body {
    padding: 1rem;
    max-height: calc(95vh - 120px);
  }
}

/* Alert Modal Specific Styles */
.alert-modal {
  max-width: 400px;
  z-index: 1060;
}

.alert-modal .modal-header {
  background-color: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.alert-modal .modal-footer {
  background-color: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  justify-content: center;
}

.alert-modal .modal-footer .btn {
  min-width: 80px;
}

/* Transaction History Container */
.transaction-history-container {
  margin-bottom: 2rem;
}

/* Day Header Card */
.day-header-card {
  border-radius: 0.5rem;
  padding: 1.5rem 1.25rem;
  margin-bottom: 0.75rem;
  top: 0;
  z-index: 10;
}

.day-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.day-date {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
  display: flex;
  align-items: center;
}

.day-date i {
  color: var(--text-secondary);
  opacity: 0.8;
}

.day-summary {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.expense-summary {
  color: var(--expense-color);
  font-weight: 600;
  font-size: 0.875rem;
  background: color-mix(in srgb, var(--expense-color) 10%, transparent);
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--expense-color) 20%, transparent);
}

.income-summary {
  color: var(--income-color);
  font-weight: 600;
  font-size: 0.875rem;
  background: color-mix(in srgb, var(--income-color) 10%, transparent);
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--income-color) 20%, transparent);
}

.neutral-summary {
  color: var(--text-secondary);
  font-style: italic;
  font-size: 0.875rem;
}

/* Transaction Card */
.transaction-card {
  border: none;
  background: var(--bg-card);
  margin-bottom: 0.875rem;
}

.transaction-card .card-body {
  padding: 1.25rem 1.5rem !important;
}

.transaction-card.expense-card {
  border-left: 6px solid var(--expense-color);
}

.transaction-card.income-card {
  border-left: 6px solid var(--income-color);
}

.savings-card {
  border-left: 6px solid var(--income-color);
}

.income-card {
  border-left: 6px solid var(--income-color);
}

.transaction-description {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;
  word-break: break-word;
}

.transaction-category {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
}

.transaction-category i {
  font-size: 0.75rem;
}

/* Transaction amount styles moved to global style.css */

/* Empty State Card */
.empty-state-card {
  border: none;
  background: var(--bg-card);
}

.empty-state-card i {
  display: block;
  color: var(--text-secondary);
}

.empty-state-card p,
.empty-state-card small {
  color: var(--text-secondary);
}

/* Responsive Design */
@media (max-width: 768px) {
  .day-header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .day-date {
    font-size: 0.95rem;
  }

  .day-summary {
    gap: 0.5rem;
  }

  .transaction-description {
    font-size: 0.9rem;
  }
}

@media (max-width: 576px) {
  .day-date {
    font-size: 0.875rem;
  }

  .expense-summary,
  .income-summary {
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
  }

  .transaction-card .card-body {
    padding: 1.125rem 1.25rem !important;
  }

  .transaction-description {
    font-size: 0.85rem;
  }

  .delete-btn i {
    font-size: 1.15rem;
  }
}

/* Fixed Trash Icon Styles - Moved to CustomHeader component */

/* Transaction Stats Card */
.transaction-stats-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 1rem;
  transition: all 0.3s ease;
}

.stats-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.stats-title {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.stats-content {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat-label {
  font-size: 0.8rem;
  color: var(--text-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Responsive adjustments for stats */
@media (max-width: 576px) {
  .stats-content {
    gap: 1rem;
  }

  .stat-item {
    flex: 1;
    min-width: 0;
  }

  .stat-value {
    font-size: 0.9rem;
  }
}

/* Dark Mode Toggle Styles */
.dark-mode-toggle {
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
}

.color-swatch {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  margin-right: 0.5rem;
  border: 2px solid rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.color-indicator {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #ffffff;
  transition: all 0.3s ease;
}

.color-swatch.dark .color-indicator {
  background: #1a1a1a;
}

/* Dark Mode Support */
:global([data-theme]) .month-navigator-card {
  border-color: var(--border-color);
}

:global([data-theme]) .nav-btn {
  border-color: #66b3ff;
  color: #66b3ff;
}

:global([data-theme]) .nav-btn:hover:not(:disabled) {
  background: #66b3ff;
  color: #1a1a1a;
}

:global([data-theme]) .nav-btn:disabled {
  border-color: var(--border-color);
  color: var(--text-secondary);
}

/* Category Selector Styles */
.category-selector .card {
  background: var(--bg-card);
  transition: all 0.3s ease;
}

.category-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
}

.category-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  position: relative;
  min-width: 140px;
  justify-content: center;
}

.category-btn i {
  font-size: 1rem;
}

/* Dashboard Container */
.dashboard-container {
  position: relative;
}

.api-loading-container .card {
  border-radius: 5px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.api-loading-container .card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.api-loading-container .header-content {
  flex: 1;
  text-align: left;
}

.loading-spinner {
  position: relative;
  display: inline-block;
}

.loading-spinner .spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3em;
  animation: spin 1s linear infinite;
}

.api-loading-container .card-body p {
  color: var(--text-secondary);
  font-size: 0.95rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Category Selector Styles */
.category-selector .card {
  border-radius: 5px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.category-selector .card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.category-selector .header-content {
  flex: 1;
  text-align: left;
}

.category-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
}

.category-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  position: relative;
  min-width: 140px;
  justify-content: center;
}

.category-btn i {
  font-size: 1rem;
}

.category-btn .card-count {
  background: rgba(255, 255, 255, 0.2);
  color: inherit;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.25rem;
}

.category-btn.btn-primary .card-count {
  background: rgba(255, 255, 255, 0.25);
  color: white;
}

/* Responsive adjustments for loading */
@media (max-width: 768px) {
  .loading-spinner .spinner-border {
    width: 2.5rem;
    height: 2.5rem;
  }

  .api-loading-container .card-body p {
    font-size: 0.9rem;
  }
}

/* Responsive adjustments for category selector */
@media (max-width: 1200px) {
  .category-buttons {
    gap: 0.5rem;
  }

  .category-btn {
    min-width: 120px;
    padding: 0.625rem 1rem;
    font-size: 0.85rem;
  }
}

@media (max-width: 992px) {
  .category-btn {
    min-width: 110px;
    padding: 0.5rem 0.875rem;
    font-size: 0.8rem;
  }

  .category-btn i {
    font-size: 0.9rem;
  }
}

/* Hide category selector on mobile - cards show all by default */
@media (max-width: 768px) {
  .category-selector {
    display: none !important;
  }
}

/* Mobile Category Navigation */
.mobile-category-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--bg-card);
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
}

.category-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.category-dots {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.category-dots .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--border-color);
  transition: all 0.3s ease;
  cursor: pointer;
}

.category-dots .dot.active {
  width: 24px;
  border-radius: 4px;
  background: #36A2EB;
}

.category-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
}

.category-label i {
  color: #36A2EB;
  font-size: 1.1rem;
}

.card-count-badge {
  background: rgba(54, 162, 235, 0.15);
  color: #36A2EB;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.25rem;
}

.swipe-hint {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  opacity: 0.7;
}

.swipe-hint i {
  font-size: 0.875rem;
}

/* Dashboard masonry with swipe support */
.dashboard-masonry {
  touch-action: pan-y;
  user-select: none;
  transition: transform 0.3s ease;
}

/* Smooth transitions for category changes on mobile */
@media (max-width: 768px) {
  .masonry-item {
    animation: fadeInUp 0.4s ease;
  }

  /* Hide TodayRemaining card completely on mobile when viewing past months */
  .masonry-item.hide-on-mobile-past-month {
    display: none;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dark mode adjustments */
.dark-mode .mobile-category-nav {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.dark-mode .card-count-badge {
  background: rgba(54, 162, 235, 0.25);
}


/* Description Input with Price Highlighting */
.description-input {
  min-height: 48px;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.description-input:empty:before {
  content: attr(data-placeholder);
  color: #6c757d;
  pointer-events: none;
}

.dark-mode .description-input:empty:before {
  color: #b3b3b3;
}

.description-input:focus {
  outline: none;
}

.price-highlight {
  background-color: rgba(64, 192, 87, 0.2);
  color: var(--income-color);
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid rgba(64, 192, 87, 0.3);
  transition: all 0.2s ease;
}

.dark-mode .price-highlight {
  background-color: rgba(64, 192, 87, 0.25);
  border-color: rgba(64, 192, 87, 0.4);
}
</style>

<style>
/* Unscoped styles for dynamically inserted content */
.price-highlight {
  background-color: rgb(220, 53, 69) !important;
  color: #fff !important;
  font-weight: 600 !important;
  padding: 2px 4px !important;
  border-radius: 4px !important;
  border: 1px solid rgb(220, 53, 69) !important;
  transition: all 0.2s ease !important;
}

.date-highlight {
  background-color: rgb(33, 37, 41) !important;
  color: #fff !important;
  font-weight: 600 !important;
  padding: 2px 4px !important;
  border-radius: 4px !important;
  border: 1px solid rgb(33, 37, 41) !important;
  transition: all 0.2s ease !important;
}

.tag-highlight {
  background-color: var(--header-gradient-e) !important;
  color: #fff !important;
  font-weight: 600 !important;
  padding: 3px 4px !important;
  border-radius: 4px !important;
  transition: all 0.2s ease !important;
}
</style>
